import React, { useState, useEffect, useMemo, useRef } from "react";
import "./Startassessment.css";
import restart from "../assets/restart.png";
import Frame from "../assets/Frame.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookOpenReader, faCopy, faDownload, faLink, faStar, faTriangleExclamation, faLocationDot, faSliders, faCircleExclamation, faBullseye, faCaretDown, faCaretUp, faCheck, faDotCircle, faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import infoIcon from "../assets/infoicon1.png";
import rightarrow from "../assets/rightarrow.png";
import { FaInfo, FaFileAlt, FaFolder, FaTimes } from "react-icons/fa";
import { paginateCanvasToPdf } from "./pdfBreakHelper";
import Toast from "./Toast";
import RichText from "./RichText";
import {
    pickInheritanceSession,
    orderHistoryGroups,
    formatHistoryAnswer,
    isInitialAppointmentType,
    formatAppointmentLabel,
} from "./assessmentHistory";
import { API_BASE } from "../apiConfig";

// ---------------------------------------------------------------------------
// GOAL DOMAIN TEXT CLEANUP
// ---------------------------------------------------------------------------

// Some pathway rows store the goal domain with the domain repeated after a
// hyphen, e.g. "Play and Cognition-Play and Cognition". Compare with a
// normalised form (case, "&" and punctuation removed) so the duplicate is
// detected and collapsed to a single copy.
const normalizeGoalDomain = (s) =>
    String(s || "")
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]/g, "");

const cleanGoalDomainText = (d) => {
    const parts = String(d || "")
        .split("-")
        .map((p) => p.trim())
        .filter(Boolean);
    if (parts.length > 1) {
        const firstNorm = normalizeGoalDomain(parts[0]);
        if (firstNorm && parts.every((p) => normalizeGoalDomain(p) === firstNorm)) {
            return parts[0];
        }
    }
    return d;
};

// ---------------------------------------------------------------------------
// NETWORK / TIMEOUT UTILITIES
// ---------------------------------------------------------------------------

// All network calls in this file go through this wrapper so that a slow
// or dropped connection is detected quickly instead of hanging forever.
// If a request times out or the network fails, it notifies the component
// (via globalNetworkErrorHandler, registered by the component on mount)
// so a popup can be shown to the user.
const API_TIMEOUT_MS = 10000;

let globalNetworkErrorHandler = null;

const fetchWithTimeout = async (url, options = {}, timeout = API_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (globalNetworkErrorHandler) globalNetworkErrorHandler();
    throw error;
  }
};

const dispatchSessionExpired = () => {
  window.dispatchEvent(new CustomEvent("session-expired"));
};

const isTokenError = (json) => {
  if (!json || json.status !== false || !json.message) return false;
  const msg = json.message.toLowerCase();
  return (msg.includes("invalid") && msg.includes("token"))
    || (msg.includes("expired") && msg.includes("token"))
    || msg.includes("token already logged out")
    || msg.includes("token missing");
};

const isGoalDomainQuestion = (q) => {
  const text = String(q?.question_text || q?.question || "").toLowerCase();
  const keyword = String(q?.keywords || "").toLowerCase();
  return keyword.includes("goal domain")
    || text.includes("domain of the new goal")
    || text.includes("domain of goal");
};

// ---------------------------------------------------------------------------
// API UTILITIES
// ---------------------------------------------------------------------------

const saveResponseAPI = async (sessionId, node, entity, entityId, answer = null, answers = null) => {
  if (!sessionId) { console.warn("Cannot save response: No Session ID"); return; }
  const token = localStorage.getItem("token");
  const url = `${API_BASE}/therapist_api/save_response.php`;
  const payload = { sessionId: parseInt(sessionId), node, entity, entityId: parseInt(entityId) };
  if (answers) { payload.answers = answers.map(id => parseInt(id)); } else { payload.answer = answer; }
  try {
    const response = await fetchWithTimeout(url, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(payload) });
    const json = await response.json();
    if (json.status) { console.log(`? Saved Node [${node}]:`, json.message); retryPendingResponses(); }
    else {
      console.error(`? Failed to save Node [${node}]:`, json.message);
      if (isTokenError(json)) { dispatchSessionExpired(); return; }
    }
  } catch (error) { console.error("Network error saving response:", error.message); queuePendingResponse(payload); }
};

const saveHineHaiAPI = async (sessionId, node, type, questionId, value, entries = []) => {
  if (!sessionId) { console.warn("Cannot save HINE/HAI: No Session ID"); return; }
  const token = localStorage.getItem("token");
  const url = `${API_BASE}/therapist_api/save_hine_hai.php`;
  const payload = { sessionId: parseInt(sessionId), node, type, questionId: parseInt(questionId), value };
  if (value === "Yes" && entries.length > 0) { payload.entries = entries; }
  try {
    const response = await fetchWithTimeout(url, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(payload) });
    const json = await response.json();
    if (json.status) { console.log(`? Saved ${type.toUpperCase()} Node [${node}]:`, json.message); }
    else {
      console.error(`? Failed to save ${type.toUpperCase()} Node [${node}]:`, json.message);
      if (isTokenError(json)) { dispatchSessionExpired(); return; }
    }
  } catch (error) { console.error("Network error saving HINE/HAI:", error.message); queuePendingResponse(payload); }
};

// ---------------------------------------------------------------------------
// NETWORK RESILIENCE ? queue failed requests & retry when possible
// ---------------------------------------------------------------------------

const PENDING_RESPONSES_KEY = "pendingResponses";

const queuePendingResponse = (payload) => {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_RESPONSES_KEY) || "[]");
    pending.push({ payload, timestamp: Date.now() });
    localStorage.setItem(PENDING_RESPONSES_KEY, JSON.stringify(pending));
  } catch (e) { console.error("Failed to queue pending response:", e); }
};

const retryPendingResponses = async () => {
  let pending;
  try { pending = JSON.parse(localStorage.getItem(PENDING_RESPONSES_KEY) || "[]"); } catch { return; }
  if (!pending.length) return;
  const token = localStorage.getItem("token");
  if (!token) return;
  const remaining = [];
  for (const item of pending) {
    try {
      const response = await fetch(
        `${API_BASE}/therapist_api/save_response.php`,
        { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(item.payload) }
      );
      const json = await response.json();
      if (json.status) console.log(`? Retried Node [${item.payload.node}]`);
      else {
        if (isTokenError(json)) { dispatchSessionExpired(); return; }
        remaining.push(item);
      }
    } catch { remaining.push(item); }
  }
  localStorage.setItem(PENDING_RESPONSES_KEY, JSON.stringify(remaining));
};

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

const getQuestionType = (question) => {
  if (!question) return { isHINE: false, isHAI: false };
  if (question.type) {
    const t = question.type.toString().toUpperCase().trim();
    return { isHINE: t === "HINE", isHAI: t === "HAI" };
  }
  const instructionString = question.instruction || "";
  const result = { isHINE: false, isHAI: false };
  if (!instructionString) return result;
  if (instructionString.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(instructionString);
      if (Array.isArray(parsed)) {
        parsed.forEach(item => {
          if (typeof item === "string") {
            const lower = item.trim().toLowerCase();
            if (lower === "hine") result.isHINE = true;
            if (lower === "hai") result.isHAI = true;
          }
        });
        return result;
      }
    } catch { /* fall through */ }
  }
  const parts = instructionString.split(",").map(s => s.trim().toLowerCase());
  if (parts.includes("hine")) result.isHINE = true;
  if (parts.includes("hai")) result.isHAI = true;
  return result;
};

const parseInstructions = (instructionString) => {
  const instructions = { isMulti: false, isVertical: false, isHINE: false, isHAI: false };
  if (!instructionString) return instructions;
  if (instructionString.trim().startsWith("[")) {
    try {
      const parsedArray = JSON.parse(instructionString);
      if (Array.isArray(parsedArray)) {
        parsedArray.forEach(item => {
          if (typeof item === "string") {
            const lowerItem = item.trim().toLowerCase();
            if (lowerItem === "multi") instructions.isMulti = true;
            if (lowerItem === "vertical") instructions.isVertical = true;
            if (lowerItem === "hine") instructions.isHINE = true;
            if (lowerItem === "hai") instructions.isHAI = true;
          }
        });
        return instructions;
      }
    } catch { /* fall through */ }
  }
  const parts = instructionString.split(",").map(s => s.trim().toLowerCase());
  if (parts.includes("multi")) instructions.isMulti = true;
  if (parts.includes("vertical")) instructions.isVertical = true;
  if (parts.includes("hine")) instructions.isHINE = true;
  if (parts.includes("hai")) instructions.isHAI = true;
  return instructions;
};

const parseSubOptionLimit = (instruction) => {
  if (!instruction) return 1;
  const match = instruction.match(/select\s+(\d+)\s+options/i);
  if (match && match[1]) return parseInt(match[1]);
  return 1;
};

const instructionHasEnd = (instructionString) => {
  if (!instructionString) return false;
  if (instructionString.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(instructionString);
      if (Array.isArray(parsed)) {
        return parsed.some(
          (item) => typeof item === "string" && item.trim().toLowerCase() === "end"
        );
      }
    } catch { /* fall through to comma-separated parse */ }
  }
  const parts = instructionString.split(",").map((s) => s.trim().toLowerCase());
  return parts.includes("end");
};

const hasEndInstructionDeep = (obj) => {
  if (!obj || typeof obj !== "object") return false;
  if (instructionHasEnd(obj.instruction)) return true;
  return [
    obj.options,
    obj.sub_options,
    obj.sub_questions,
    obj.questions,
    obj.groups,
  ].some((arr) => Array.isArray(arr) && arr.some(hasEndInstructionDeep));
};

// Given the current question and the user's answers, follow the *selected*
// option / sub-option chain and report whether any answered node carries the
// "End" instruction. Sub-questions are separate steps, so they are reached
// as their own question and checked by handleNext when it is their turn.
const selectedChainHasEnd = (question, answers, visited = new Set()) => {
  if (!question) return false;
  if (instructionHasEnd(question.instruction)) return true;
  const qNode = question.node_number;
  if (visited.has(qNode)) return false;
  visited.add(qNode);

  const ans = answers[qNode];
  if (!ans) return false;
  const selectedTexts = Array.isArray(ans) ? ans : [ans];

  const walk = (options, texts, pathTexts) => {
    for (const opt of options) {
      if (!texts.includes(opt.option_text)) continue;
      if (instructionHasEnd(opt.instruction)) return true;
      if (opt.sub_options && opt.sub_options.length > 0) {
        const nestedKey = [qNode, ...pathTexts, opt.option_text, "sub"].join("_");
        const nestedAns = answers[nestedKey];
        if (nestedAns) {
          const nestedTexts = Array.isArray(nestedAns) ? nestedAns : [nestedAns];
          if (walk(opt.sub_options, nestedTexts, [...pathTexts, opt.option_text])) return true;
        }
      }
    }
    return false;
  };

  return walk(question.options || [], selectedTexts, []);
};

// ---------------------------------------------------------------------------
// APPOINTMENT TYPES
// ---------------------------------------------------------------------------
// Three mutually-exclusive appointment options are offered on step 1:
//
//   Initial           - one-time first assessment for a brand new client.
//                       Server-enforced as once-per-client.
//   Review           - set a NEW goal for a client who already has an
//                       initial assessment. Repeatable any number of times.
//   Review Smart Goal - launched from the goalreview "Review Assessment"
//                       button. Reviews the goal that already exists on the
//                       originating pathway and revises it end-to-end.
//
// Both "Review" and "Review Smart Goal" run against the `review` pathway and
// store a review-appointment session, which is why the "Review Existing
// Goals" table only ever shows Initial / Review. "Review Smart Goal" is an
// additional UI pathway on top of a review session, carried through the URL
// together with the originating goal session id.
// ---------------------------------------------------------------------------

const APPT_INITIAL = "Initial";
const APPT_REVIEW = "Review";
const APPT_REVIEW_SMART_GOAL = "Review Smart Goal";

const APPOINTMENT_OPTIONS = [APPT_INITIAL, APPT_REVIEW, APPT_REVIEW_SMART_GOAL];

// Normalises every accepted spelling of the ?appointment= URL param so the
// header cards, goalreview and the in-form radios all resolve to one value.
const normaliseAppointmentParam = (raw) => {
  const value = String(raw || "").trim().toLowerCase();
  if (!value) return "";
  if (value === "initial") return APPT_INITIAL;
  if (value === "review smart goal" || value === "reviewsmartgoal" || value === "smart goal" || value === "smartgoal") {
    return APPT_REVIEW_SMART_GOAL;
  }
  if (value === "review") return APPT_REVIEW;
  return "";
};

// Review and Review Smart Goal both operate on an existing client, so the
// carried-over ?client= / ?goalSessionId= context stays valid for either.
const isReviewOption = (value) => value === APPT_REVIEW || value === APPT_REVIEW_SMART_GOAL;

// -- Keyword tolerance for cross-pathway answer inheritance -------------------
// A Review Smart Goal pathway is a copy of the Review/Initial pathway with
// re-worded questions, so an exact question_text match is often impossible and
// the keyword alone is too brittle:
//   Initial "Neuroimaging"      -> RSG "Neuroimaging results"
//   Initial "Communication"     -> RSG "Communication skill"   (Current Skills)
//   Initial "Communication"     -> RSG "Communication"          (Comorbidities)
// Normalising drops the filler words that only describe *freshness* of the
// answer rather than its subject, so the two variants above collapse onto the
// same key. Filler is deliberately narrow: only words that qualify "give me a
// new reading of X" and cannot be the subject of a question on their own.
const KEYWORD_FILLER_WORDS = new Set([
  "result", "results", "available", "availability", "updated", "update", "new",
  "any", "current", "latest", "recent", "information", "info", "score", "scores",
]);

const normaliseKeyword = (raw) => {
  const tokens = String(raw == null ? "" : raw)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter(w => !KEYWORD_FILLER_WORDS.has(w));
  return tokens.join(" ").trim();
};

// Shortest form we will accept as a prefix/suffix match, so an abbreviation can
// never hijack an unrelated question.
const MIN_FUZZY_KEYWORD_LEN = 6;

// ---------------------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------------------

export default function Startassessment() {
  const location = useLocation();
  const navigate = useNavigate();
  // Memoised on location.search so the identity is stable between renders.
  // Anything that lists urlParams in a dependency array (e.g. historyClientId
  // below) would otherwise see a brand-new object on every single render.
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlAppointment = urlParams.get("appointment");

  const [session, setSession] = useState(normaliseAppointmentParam(urlAppointment));
  // The entry point that loaded this page (?appointment=Initial / Review /
  // Review Smart Goal). The step-1 appointment radio group only offers the
  // option that matches the entry point: "Start Initial Client Assessment"
  // shows Initial alone, "Set New Goal for Existing Client" shows Review
  // alone, and the goalreview "Review Assessment" button shows Review Smart
  // Goal alone. A direct /start-assessment visit with no param keeps all
  // three selectable (normaliseAppointmentParam("") returns "").
  const [entryAppointment, setEntryAppointment] = useState(normaliseAppointmentParam(urlAppointment));
  const [client, setClient] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [fev, setfevo] = useState("");
  const [showInfantInfo, setShowInfantInfo] = useState(false);
  const [pathwaysData, setPathwaysData] = useState([]);
  const [activePathway, setActivePathway] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isRestoringDraft, setIsRestoringDraft] = useState(false);

  const targetNodeRef = useRef(null);

  const [answers, setAnswers] = useState({});
  const isMobile = window.innerWidth <= 768;

  const [hineData, setHineData] = useState([
    { id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }
  ]);
  const [haiData, setHaiData] = useState([
    { id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }
  ]);

  const [interventionsData, setInterventionsData] = useState([]);
  const [activeGoalTab, setActiveGoalTab] = useState(null);

  const [showLeftPanel, setShowLeftPanel] = useState(false);
  const [step, setStep] = useState(1);
  const [isGoalDetailStep, setIsGoalDetailStep] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState({});
  const [isFavourite, setIsFavourite] = useState(false);
  const [openSections, setOpenSections] = useState({ demographics: false, background: false, comorbidities: false });

  // -- NEW: Share modal state ------------------------------------------------
  const [showShareModal, setShowShareModal] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const summaryRef = useRef(null);
  const [toast, setToast] = useState(null);

  // -- NEW: Network issue popup state ----------------------------------------
  const [networkError, setNetworkError] = useState(false);

  // -- NEW: Goal Domain "Eating and Drinking" single-choice popup state ------
  const [showGoalDomainPopup, setShowGoalDomainPopup] = useState(false);

  // -- Review-flow state (merged from Review-assessment.js) -----------------
  const [reviewPathwaysData, setReviewPathwaysData] = useState([]);
  // Client names present on the "Review Existing Goals" page (sessions that
  // actually have saved goals). The Review / Review Smart Goal client dropdown
  // shows ONLY these clients - a Review appointment reviews existing goals, so
  // a client with no saved goals has nothing to review. null = eligibility
  // still loading (dropdown keeps all clients), [] = loaded, no eligible
  // client, [names] = loaded and restricted to the table's clients.
  const [goalsEligibleClients, setGoalsEligibleClients] = useState(null);
  const [selectedReviewSessionId, setSelectedReviewSessionId] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showReviewGoalScreen, setShowReviewGoalScreen] = useState(false);

  // -- NEW: Assessment history for the selected client. Serves two purposes:
  // 1. the data-continuity pre-fill (Background Information & Assessments ->
  //    Comorbidities are carried over from the last completed session), and
  // 2. the "Previous Data" panel opened from the folder icon on every
  //    question-card, which lists every past Initial + Review session. -------
  const [initialHistory, setInitialHistory] = useState(null);
  const [groupHistory, setGroupHistory] = useState(null);
  const [historyGroupName, setHistoryGroupName] = useState(null);
  const [showHistoryFiles, setShowHistoryFiles] = useState(false);
  const [loadingGroupHistory, setLoadingGroupHistory] = useState(false);
  const [isDownloadingGroupData, setIsDownloadingGroupData] = useState(false);
  const groupHistoryPrintRef = useRef(null);
  const historyViewRef = useRef(null);

  // -- Review Smart Goal state ---------------------------------------------
  // `goalSessionId` is the originating session that holds the goal being
  // reviewed. It is carried from goalreview -> start-assessment ->
  // Interventions -> Updategoal -> goalsetdownload so the revision always
  // lands on the correct goal row.
  const [goalSessionId, setGoalSessionId] = useState(
    urlParams.get("goalSessionId") || urlParams.get("goalSession") || null
  );
  const [smartGoalContext, setSmartGoalContext] = useState(null);
  const [smartGoalLoading, setSmartGoalLoading] = useState(false);

  // -- Initial-assessment eligibility (Initial can only be done once) ------
  const [initialBlocked, setInitialBlocked] = useState(null);

  // "Review" and "Review Smart Goal" both run the `review` pathway.
  const isReviewMode = session === APPT_REVIEW || session === APPT_REVIEW_SMART_GOAL;
  const isSmartGoalMode = session === APPT_REVIEW_SMART_GOAL;

  const token = localStorage.getItem("token");
  const activeStepRef = useRef(null);
  const localResumeStepRef = useRef(null);
  const stateRef = useRef({});
  const lastAppointmentSessionRef = useRef(session);

  // -- NEW: Register this component so any API call (including the ones in
  // saveResponseAPI / saveHineHaiAPI outside the component) can surface a
  // network-issue popup immediately when a request times out or fails ------
  useEffect(() => {
    globalNetworkErrorHandler = () => setNetworkError(true);
    return () => { globalNetworkErrorHandler = null; };
  }, []);

  // -- Retry pending responses on mount -------------------------------------
  useEffect(() => { retryPendingResponses(); }, []);

  // -- Keep stateRef in sync with latest values (for beforeunload) ----------
  useEffect(() => { stateRef.current = { session, client, ageRange, answers, hineData, haiData, currentSessionId, openSections, showReviewGoalScreen, step, goalSessionId }; });
  useEffect(() => {
    const handleBeforeUnload = () => { localStorage.setItem("assessmentState", JSON.stringify(stateRef.current)); };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => { window.removeEventListener("beforeunload", handleBeforeUnload); };
  }, []);

  // -- Persist state to localStorage ----------------------------------------
  // goalSessionId is persisted too: /summarypage is reached by a plain
  // navigate() with no query string, so without this the goal-revision context
  // was lost on the way to the summary and "Save and View Intervention" fell
  // back to creating a new goal instead of revising the existing one.
  useEffect(() => {
    const stateToSave = { session, client, ageRange, answers, hineData, haiData, currentSessionId, openSections, showReviewGoalScreen, step, goalSessionId };
    localStorage.setItem("assessmentState", JSON.stringify(stateToSave));
  }, [session, client, ageRange, answers, hineData, haiData, currentSessionId, openSections, fev, showReviewGoalScreen, step, goalSessionId]);

  // -- Restore state from localStorage --------------------------------------
  // ?appointment= stays in the URL (kept so a reload boots the same
  // appointment page), so it must NO LONGER blanket-skip the restore. Rules:
  //   * A resolved draft (?sessionId=) or a client carried over from another
  //     page (?client=) drives the form - nothing is restored.
  //   * Otherwise a saved draft is restored ONLY when it belongs to the SAME
  //     appointment type as the URL entry (or the URL carries no appointment),
  //     so "Start Initial Client Assessment" never resumes a Review draft.
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlSessionId = urlParams.get("sessionId");
    const urlAppointment = urlParams.get("appointment");
    if (urlSessionId || urlParams.get("client")) { console.log("URL drives the session - skipping localStorage restore."); return; }
    const savedState = localStorage.getItem("assessmentState");
    if (!savedState) return;
    try {
      const parsed = JSON.parse(savedState);
      if (urlAppointment && normaliseAppointmentParam(urlAppointment) !== (parsed.session || "")) {
        // A different appointment type was explicitly requested - start it fresh.
        return;
      }
      setSession(parsed.session || "");
      setClient(parsed.client || "");
      setAgeRange(parsed.ageRange || "");
      setIsFavourite(Boolean(parsed.is_favourite));
      if (parsed.step && parsed.step > 1) localResumeStepRef.current = parsed.step;
      setStep(1);
      setAnswers(parsed.answers || {});
      setHineData(parsed.hineData || [{ id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }]);
        setHaiData(parsed.haiData || [{ id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }]);
        setCurrentSessionId(parsed.currentSessionId || null);
        setOpenSections(parsed.openSections || { demographics: false, background: false, comorbidities: false });
        setShowReviewGoalScreen(Boolean(parsed.showReviewGoalScreen));
    } catch (e) { console.error("Error parsing saved state", e); }
  }, [location.search]);

  // -- The appointment param pre-selects the initial/review option on step 1.
  // It is KEPT in the URL (normalised) so a browser reload restores the same
  // appointment page instead of an empty step-1 shell. This runs on every URL
  // change (not just mount) so switching between "Start Initial Client
  // Assessment" and "Review Session to Set New Goal" from the header/cards
  // while the component stays mounted also re-selects the right option.
  // Crossing between Initial and Review resets the step-1 form so no stale
  // state carries over. --
  useEffect(() => {
    lastAppointmentSessionRef.current = session;
  }, [session]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlAppointment = params.get("appointment");
    if (!urlAppointment) return;
    const desired = normaliseAppointmentParam(urlAppointment);
    if (!desired) return;
    // Lock the step-1 appointment radio group to this entry point - the
    // header/dashboard card that loaded the page already decided the session
    // type, so the other two options are hidden for the whole visit.
    setEntryAppointment(desired);
    const hasClientParam = params.get("client");
    // Keep the appointment so the URL is itself the reload handle; only
    // rewrite the address when the raw spelling needs normalising.
    if (urlAppointment !== desired) {
      params.set("appointment", desired);
      const qs = params.toString();
      navigate(qs ? `/start-assessment?${qs}` : "/start-assessment", { replace: true });
    }

    setSession(desired);
    // The Review Smart Goal entry point always carries the originating goal
    // session, so keep it in state when landing on that pathway.
    if (desired === APPT_REVIEW_SMART_GOAL) {
      const gsid = params.get("goalSessionId") || params.get("goalSession");
      if (gsid) setGoalSessionId(gsid);
    }
    if (lastAppointmentSessionRef.current !== desired) {
      setShowReviewGoalScreen(false);
      setStep(1);
      setClientSearchTerm("");
      setInitialBlocked(null);
      if (desired !== APPT_REVIEW_SMART_GOAL) setGoalSessionId(null);
      if (!hasClientParam) {
        setClient("");
        setAgeRange("");
      }
      setAnswers({});
      setHineData([{ id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }]);
      setHaiData([{ id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSessionChange = (e) => {
    const value = e.target.value;
    setSession(value);
    setClientSearchTerm("");
    setShowReviewGoalScreen(false);
    setInitialBlocked(null);
    if (value !== APPT_REVIEW_SMART_GOAL) setGoalSessionId(null);

    // When arriving from the goalreview "Review Assessment" button the URL
    // carries a client name (?client=<name>). Switching away from the
    // review-based options must drop that prefilled carryover so the Initial
    // form starts fresh.
    if (!isReviewOption(value)) {
      const params = new URLSearchParams(location.search);
      if (params.get("client")) {
        setClient("");
        setAgeRange("");
        params.delete("client");
        params.delete("appointment");
        params.delete("goalSessionId");
        params.delete("goalSession");
        const qs = params.toString();
        navigate(qs ? `/start-assessment?${qs}` : `/start-assessment`, { replace: true });
      }
    }
  };
  // -- Fetch draft from API when sessionId is in URL ------------------------
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get("sessionId");
    if (!sessionId || !token) return;

    const fetchDraft = async () => {
      setIsRestoringDraft(true);
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_draft_session_detail.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({ sessionId: parseInt(sessionId) })
          }
        );
        const json = await response.json();
        if (!json.status || !json.data) {
          if (isTokenError(json)) { dispatchSessionExpired(); return; }
          return;
        }

        const draftData = json.data;

        setCurrentSessionId(draftData.sessionId);

        setClient(draftData.client_name || "");
        setAgeRange(draftData.age || "");
        setfevo(draftData.is_favourite || "");
        setIsFavourite(Boolean(draftData.is_favourite));

        // Restore the EXACT session mode. "Review" and "Review Smart Goal" both
        // run the `review` pathway, so the pathway value cannot tell them apart
        // - collapsing on `!== "initial"` reopened every Review Smart Goal draft
        // in plain Review mode. get_draft_session_detail.php returns the
        // per-session label persisted in node 1; appointment_key /
        // appointment_label are authoritative, with the old fields kept as
        // fallbacks for drafts written before the mode was recorded.
        let sessionType = APPT_INITIAL;
        const modeKey = String(draftData.appointment_key || "").trim().toLowerCase();
        if (modeKey === "review_smart_goal") {
          sessionType = APPT_REVIEW_SMART_GOAL;
        } else if (modeKey === "review") {
          sessionType = APPT_REVIEW;
        } else if (modeKey === "initial") {
          sessionType = APPT_INITIAL;
        } else {
          const modeLabel = String(draftData.appointment_label || "").trim();
          if (/smart/i.test(modeLabel)) {
            sessionType = APPT_REVIEW_SMART_GOAL;
          } else if (/review/i.test(modeLabel)) {
            sessionType = APPT_REVIEW;
          } else if (draftData.appointment_type) {
            sessionType = String(draftData.appointment_type).toLowerCase() === "initial"
              ? APPT_INITIAL
              : APPT_REVIEW;
          } else if (draftData.appt_id !== undefined) {
            sessionType = draftData.appt_id === 1 ? APPT_INITIAL : APPT_REVIEW;
          }
        }
        setSession(sessionType);

        // Resuming an existing REVIEW session should skip the step-1 setup
        // screen (whose "Next" would create a NEW review session and push the
        // user back to Goal Progress Update / goalreview). Jump straight into
        // the review questions instead; the last_node effect overrides this
        // step when the draft has a recorded position.
        //
        // BOTH review modes must skip step 1. This compared against the literal
        // "Review", so a "Review Smart Goal" draft fell through, dropped the
        // therapist back onto the step-1 form and re-rendered the appointment
        // selector as a plain Review.
        if (isReviewOption(sessionType)) {
          setShowReviewGoalScreen(false);
          setStep(2);
        }

        const newAnswers = {};
        const draftPathway = draftData.pathway || [];
        let restoredHineData = [];
        let restoredHaiData = [];

        const normaliseAnswer = (raw) => {
          if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            return {
              value: raw.value ?? null,
              entries: Array.isArray(raw.entries) ? raw.entries : []
            };
          }
          if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === "object") {
            return { value: "Yes", entries: raw };
          }
          if (raw !== null && raw !== undefined && !Array.isArray(raw)) {
            return { value: String(raw), entries: [] };
          }
          return { value: null, entries: [] };
        };

        const restoreSubOptions = (node, pathLabels, suboptions, parentInstruction = "") => {
          if (!suboptions || suboptions.length === 0) return;

          const instructions = parseInstructions(parentInstruction);
          const isMulti = instructions.isMulti;

          const selectedSubs = suboptions.filter(sub => sub.selected === true);
          if (selectedSubs.length === 0) return;

          const subKey = [node, ...pathLabels, "sub"].join("_");

          if (isMulti) {
            newAnswers[subKey] = selectedSubs.map(s => s.label);
          } else {
            if (selectedSubs.length === 1) {
              newAnswers[subKey] = selectedSubs[0].label;
            } else {
              newAnswers[subKey] = selectedSubs.map(s => s.label);
            }
          }

          selectedSubs.forEach(sub => {
            restoreSubOptions(node, [...pathLabels, sub.label], sub.suboptions, sub.instruction || "");
          });
        };

        draftPathway.forEach(p => {
          const node = p.node;

          const qTypeRaw = (p.type || "").toString().toUpperCase().trim();
          const isHINEQuestion = qTypeRaw === "HINE";
          const isHAIQuestion = qTypeRaw === "HAI";

          const { value: answerValue, entries } = normaliseAnswer(p.answer);

          if (isHINEQuestion) {
            if (entries.length > 0) {
              restoredHineData = entries.map((item, idx) => ({
                id: idx,
                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                hineScore: item.hine_score !== undefined ? item.hine_score : 0,
                rightAsym: item.right_asymmetry !== undefined ? item.right_asymmetry : 0,
                leftAsym: item.left_asymmetry !== undefined ? item.left_asymmetry : 0
              }));
            }
            if (answerValue) newAnswers[node] = answerValue;
            return;
          }

          if (isHAIQuestion) {
            if (entries.length > 0) {
              restoredHaiData = entries.map((item, idx) => ({
                id: idx,
                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                rightEahs: item.right_score !== undefined ? item.right_score : 0,
                leftEahs: item.left_score !== undefined ? item.left_score : 0,
                eachHandSumScore: item.sum !== undefined ? item.sum : 0,
                asymmetryIndex: item.asymmetry !== undefined ? item.asymmetry : 0
              }));
            }
            if (answerValue) newAnswers[node] = answerValue;
            return;
          }

          if (entries.length > 0) {
            const first = entries[0];
            if (Object.prototype.hasOwnProperty.call(first, "hine_score")) {
              restoredHineData = entries.map((item, idx) => ({
                id: idx,
                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                hineScore: item.hine_score !== undefined ? item.hine_score : 0,
                rightAsym: item.right_asymmetry !== undefined ? item.right_asymmetry : 0,
                leftAsym: item.left_asymmetry !== undefined ? item.left_asymmetry : 0
              }));
              if (answerValue) newAnswers[node] = answerValue;
              return;
            }
            if (Object.prototype.hasOwnProperty.call(first, "right_score")) {
              restoredHaiData = entries.map((item, idx) => ({
                id: idx,
                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                rightEahs: item.right_score !== undefined ? item.right_score : 0,
                leftEahs: item.left_score !== undefined ? item.left_score : 0,
                eachHandSumScore: item.sum !== undefined ? item.sum : 0,
                asymmetryIndex: item.asymmetry !== undefined ? item.asymmetry : 0
              }));
              if (answerValue) newAnswers[node] = answerValue;
              return;
            }
          }

          if (p.options && Array.isArray(p.options)) {
            const activeOptions = p.options.filter(opt =>
              opt.selected === true ||
              (opt.suboptions &&
                Array.isArray(opt.suboptions) &&
                opt.suboptions.some(sub => sub.selected === true))
            );

            const goalDomainQuestion = isGoalDomainQuestion(p);

            if (activeOptions.length === 1) {
              const isAnswerArray = Array.isArray(p.answer);
              newAnswers[node] = (goalDomainQuestion && isAnswerArray)
                ? [activeOptions[0].label]
                : activeOptions[0].label;

              restoreSubOptions(node, [activeOptions[0].label], activeOptions[0].suboptions, activeOptions[0].instruction || "");
            } else if (activeOptions.length > 1) {
              newAnswers[node] = activeOptions.map(opt => opt.label);
              activeOptions.forEach(opt =>
                restoreSubOptions(node, [opt.label], opt.suboptions, opt.instruction || "")
              );
            }
          }
        });

        setAnswers(newAnswers);
        if (restoredHineData.length > 0) setHineData(restoredHineData);
        if (restoredHaiData.length > 0) setHaiData(restoredHaiData);

        if (draftData.last_node) {
          targetNodeRef.current = draftData.last_node;
          console.log("?? Draft last_node stored:", draftData.last_node);
        }
      } catch (error) {
        console.error("Failed to fetch draft:", error.message);
      } finally {
        setIsRestoringDraft(false);
      }
    };

    fetchDraft();
  }, [location.search, token]);

  // -- Which create_pathway.appointment_type the current appointment runs.
  // get_pathway_details.php filters the WHOLE response by this value, so both
  // the pathway that gets hydrated AND the age groups offered in the step-1
  // "Age group" dropdown are exactly the ones that have a pathway of the
  // active appointment type:
  //
  //   Initial          -> "initial"           (initialAgeGroups)
  //   Review           -> "review"            (reviewAgeGroups)
  //   Review Smart Goal-> "review smart goal" (reviewAgeGroups)
  //
  // The two review types are deliberately NOT merged: each one only ever shows
  // the age groups that actually have a pathway of ITS OWN kind, so Review
  // never offers an age group that can only be reviewed as a Smart Goal.
  //
  // An empty appointment (a direct /start-assessment visit with no
  // ?appointment= param, where the step-1 radio group still offers all three)
  // sends nothing, so the endpoint stays unfiltered for that visit and the
  // dropdown can be filled once the therapist picks a type.
  const pathwayAppointmentFilter = useMemo(() => {
    if (isSmartGoalMode) return "review smart goal";
    if (session === APPT_REVIEW) return "review";
    if (session === APPT_INITIAL) return "initial";
    return "";
  }, [session, isSmartGoalMode]);

  // -- Fetch pathway data ----------------------------------------------------
  useEffect(() => {
    const fetchPathways = async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_pathway_details.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify(pathwayAppointmentFilter ? { appointment_type: pathwayAppointmentFilter } : {}),
          }
        );
        const json = await response.json();
        if (json.status && json.data && Array.isArray(json.data)) {
          setPathwaysData(json.data);
        } else if (json.status && json.pathways && Array.isArray(json.pathways)) {
          setPathwaysData(json.pathways);
        }
      } catch (error) { console.error("Fetch pathways failed:", error.message); }
    };
    if (token) fetchPathways();
  }, [token, pathwayAppointmentFilter]);

  // -- NEW: Fetch review pathway details (client-grouped, multi-session).
  // This powers the searchable client dropdown on step 1 when Review is
  // selected, and lets us resolve the correct sessionId for Goal Progress
  // Update. ----------------------------------------------------------------
  useEffect(() => {
    const fetchReviewPathways = async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_review_pathway_details.php`,
          { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` } }
        );
        const json = await response.json();
        if (json.status && json.data && Array.isArray(json.data)) {
          setReviewPathwaysData(json.data);
        }
      } catch (error) { console.error("Fetch review pathways failed:", error.message); }
    };
    if (token) fetchReviewPathways();
  }, [token]);

  // -- Fetch the client list that the "Review Existing Goals" table shows ---
  // get_goals_page.php is the exact source of that table, so Review-flow
  // client eligibility can never drift from what the table actually displays.
  // While the list is still loading (null) the dropdown keeps all clients so
  // step 1 never unlocks empty; once loaded it is strictly limited to the
  // clients who have saved goals.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchGoalsClients = async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_goals_page.php`,
          { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify({}) }
        );
        const json = await response.json();
        if (cancelled) return;
        if (json.status && Array.isArray(json.data)) {
          const names = json.data
            .map(row => row && row.client)
            .filter(Boolean);
          setGoalsEligibleClients([...new Set(names)].sort());
        } else {
          // Endpoint answered but unusable - keep the full list rather than
          // blanking the whole dropdown.
          setGoalsEligibleClients(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch goals-eligible clients failed:", error.message);
          setGoalsEligibleClients(null);
        }
      }
    };
    fetchGoalsClients();
    return () => { cancelled = true; };
  }, [token]);

  // -- Age groups for the dropdown ? only include age groups that have
  // at least one pathway whose appointment_type is "initial" ---------------
  const initialAgeGroups = useMemo(() => {
    return pathwaysData.filter(
      (ageObj) =>
        Array.isArray(ageObj.pathways) &&
        ageObj.pathways.some((p) => p.appointment_type === "initial")
    );
  }, [pathwaysData]);

  // -- All age groups that have at least one "review" OR "review smart goal"
  // pathway (mirrored from Review-assessment.js), used in step 1 when Review or
  // Review Smart Goal is selected.
  //
  // STRICT per appointment type: Review lists only the age groups that have a
  // `review` pathway and Review Smart Goal only the ones that have a
  // `review smart goal` pathway. They used to share one list that accepted
  // either type, so a Review could be started on an age group that only has an
  // RSG pathway (and vice versa) - the pathway then silently fell back to the
  // other kind. `get_pathway_details.php` is already filtered by the active
  // appointment, so this only re-asserts the same rule on the client side.
  const reviewAgeGroups = useMemo(() => {
    const wanted = isSmartGoalMode ? "review smart goal" : "review";
    return pathwaysData.filter(
      (ageObj) =>
        Array.isArray(ageObj.pathways) &&
        ageObj.pathways.some((p) => (p.appointment_type || "").toLowerCase() === wanted)
    ).map(ageObj => ageObj.age_group);
  }, [pathwaysData, isSmartGoalMode]);

  // -- Review-client eligibility (only clients in "Review Existing Goals") --
  // While the eligibility list is still loading (null) the raw list is used so
  // the form stays usable; once loaded, every client picker / label / age /
  // session resolution below reads ONLY eligibleReviewPathwaysData.
  const eligibleClientSet = useMemo(() => {
    return new Set(
      Array.isArray(goalsEligibleClients)
        ? goalsEligibleClients.map(n => String(n).trim().toLowerCase())
        : []
    );
  }, [goalsEligibleClients]);

  const eligibleReviewPathwaysData = useMemo(() => {
    const raw = reviewPathwaysData || [];
    if (!Array.isArray(goalsEligibleClients)) {
      // Eligibility still loading - keep current behaviour.
      return raw;
    }
    return raw.filter(
      c => c && eligibleClientSet.has(String(c.client_name || "").trim().toLowerCase())
    );
  }, [reviewPathwaysData, goalsEligibleClients, eligibleClientSet]);

  // -- Derived list of the eligible client names from get_review_pathway_details.php,
  // used to power the searchable client dropdown on step 1 (Review flow). --
  const reviewClientOptions = useMemo(() => {
    if (!eligibleReviewPathwaysData || eligibleReviewPathwaysData.length === 0) return [];
    return eligibleReviewPathwaysData
      .map(c => c.client_name)
      .filter(Boolean);
  }, [eligibleReviewPathwaysData]);

  // -- Spec: the Review client dropdown is labelled "[Client Name - Client Code]"
  // so two clients sharing a name stay distinguishable, and so the therapist can
  // match the row against the ID printed on the summ-container1 header.
  //
  // `reviewClientOptions` deliberately stays name-only: the name is what the
  // `client` state, the `?client=` URL param and save_basic_details.php expect.
  // The code is presentation only. Sessions come back newest-first
  // (ORDER BY ps.saved_at DESC), so the first non-empty code is the current one.
  const reviewClientLabels = useMemo(() => {
    const labels = {};
    (eligibleReviewPathwaysData || []).forEach(c => {
      if (!c || !c.client_name) return;
      const sessions = Array.isArray(c.sessions) ? c.sessions : [];
      const code = sessions
        .map(s => (s && (s.client_code ?? s.clientCode)) || "")
        .map(v => String(v).trim())
        .find(Boolean) || String(c.client_code || "").trim();
      labels[c.client_name] = code ? `${c.client_name} - ${code}` : c.client_name;
    });
    return labels;
  }, [eligibleReviewPathwaysData]);

  // -- Resolve the client's existing REVIEW age group ---------------------
  // A review flow must keep the client's review age even if a different age
  // is picked, so duplicate review sessions aren't created. Returns the age
  // group of the client's most recently created session OF THIS APPOINTMENT
  // TYPE (or "").
  // If no session of that type exists, falls back to the client's original age
  // group from their Initial session, since the client may have aged into a new
  // group.
  //
  // Review and Review Smart Goal are resolved SEPARATELY. This used to accept
  // both `review` and `review smart goal` sessions, so opening Review Smart
  // Goal pre-selected the age group of a plain Review session (and vice versa)
  // - an age group the RSG dropdown may not even offer, which then resolved to
  // a different pathway. `type` defaults to the appointment currently running.
  const getReviewAgeForClient = (name, type = (isSmartGoalMode ? "review smart goal" : "review")) => {
    if (!name || !eligibleReviewPathwaysData || eligibleReviewPathwaysData.length === 0) return "";
    const clientObj = eligibleReviewPathwaysData.find(c => c.client_name === name);
    if (!clientObj) return "";
    const sessions = Array.isArray(clientObj.sessions) ? clientObj.sessions : [];
    const reviewSessions = sessions.filter(
      s => (s.appointment_type || s.type || "").toLowerCase() === type
    );
    if (reviewSessions.length > 0) {
      const newest = reviewSessions.reduce((a, b) => {
        const sa = a.sessionId != null ? a.sessionId : a.session_id;
        const sb = b.sessionId != null ? b.sessionId : b.session_id;
        return Number(sb) > Number(sa) ? b : a;
      });
      return newest.age_group || newest.age || "";
    }
    // No review session yet - use the client's original age group
    const initialSessions = sessions.filter(
      s => (s.appointment_type || s.type || "").toLowerCase() === "initial"
    );
    if (initialSessions.length > 0) {
      const newest = initialSessions.reduce((a, b) => {
        const sa = a.sessionId != null ? a.sessionId : a.session_id;
        const sb = b.sessionId != null ? b.sessionId : b.session_id;
        return Number(sb) > Number(sa) ? b : a;
      });
      return newest.age_group || newest.age || "";
    }
    return "";
  };

  // NOTE: the review session is resumed server-side by save_basic_details.php
  // (it looks for an in-progress session for this client + appointment type), so
  // there is no client-side "find the review session id" helper here any more.

  // -- Prefill the client/age when arriving with ?client=<name> from
  // goalreview's "Review Assessment" button (?appointment=Review Smart Goal)
  // or from the one-time-Initial guard. The client name needs
  // reviewClientOptions to be loaded, so this runs once the review pathway
  // data is available. The appointment type already chosen in the URL always
  // wins - this effect only fills in the blanks. ---------------------------
  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(location.search);
    const urlClient = params.get("client");
    if (!urlClient) return;
    if (reviewClientOptions.length === 0) return;
    const match = reviewClientOptions.find(
      (name) => String(name).trim().toLowerCase() === String(urlClient).trim().toLowerCase()
    );
    if (!match) return;
    // Default to Review when nothing was requested (backwards compatible with
    // the old "Review Assessment" link), otherwise keep the requested option.
    if (!isReviewOption(session)) setSession(APPT_REVIEW);
    setClient(match);
    // Show the same "Name - Code" label the dropdown renders, so an
    // auto-selected client does not leave a differently-formatted box behind.
    setClientSearchTerm(reviewClientLabels[match] || match);
    setShowClientDropdown(false);
    const urlAge = params.get("age");
    const resolvedAge = urlAge || getReviewAgeForClient(match);
    if (resolvedAge) setAgeRange(resolvedAge);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewClientOptions, reviewClientLabels, token]);

  // -- Resolve the correct sessionId for the selected client + age group
  // combination so "Goal Progress Update" navigates with the session that
  // actually holds the intervention-planning goals (Goal Domain, Target
  // Date, Selected Interventions). Those goals were saved against the
  // client's INITIAL session, so the goal review must open with that session
  // - NOT with a freshly created review session, which has no goals saved
  // yet and would render "No goals have been saved for this session."
  //
  // Prefers the initial session for the matching age group, but falls back
  // to any other session so the sessionId is never null as long as the
  // client has any session. -----------------------------------------------
  useEffect(() => {
    if (!eligibleReviewPathwaysData || eligibleReviewPathwaysData.length === 0 || !client) {
      setSelectedReviewSessionId(null);
      return;
    }
    const clientObj = eligibleReviewPathwaysData.find(c => c.client_name === client);
    if (!clientObj) { setSelectedReviewSessionId(null); setSelectedClientId(null); return; }
    setSelectedClientId(clientObj.client_id != null ? clientObj.client_id : null);
    const sessions = Array.isArray(clientObj.sessions) ? clientObj.sessions : [];
    const norm = (v) => String(v == null ? "" : v).trim().toLowerCase();
    const getAge = (s) => norm(s.age_group || s.age);
    const getSid = (s) => (s.sessionId != null ? s.sessionId : s.session_id != null ? s.session_id : null);
    const isInitial = (s) =>
      (s.appointment_type || "").toLowerCase() === "initial" ||
      (s.type || "").toLowerCase() === "initial";
    const pick = (list) => list.find(isInitial) || list[0];
    const matchingSessions = ageRange ? sessions.filter(s => getAge(s) === norm(ageRange)) : [];
    // First try matching age, then fall back to all sessions
    let goalSession = (matchingSessions.length > 0 && pick(matchingSessions)) || pick(sessions);
    // If the picked session is a Review, prefer any Initial session instead
    // because goals/interventions are always stored in the Initial session
    if (goalSession && !isInitial(goalSession)) {
      const initialSession = sessions.find(isInitial);
      if (initialSession) goalSession = initialSession;
    }
    setSelectedReviewSessionId(goalSession ? getSid(goalSession) : null);
  }, [eligibleReviewPathwaysData, client, ageRange]);

  // -- NEW: Fetch the selected client's full assessment history once. Powers
  // both the data-continuity pre-fill and the "Previous Data" panel. --------
  useEffect(() => {
    let cancelled = false;
    const clientId = selectedClientId;
    if (!clientId || !token) { setInitialHistory(null); return; }
    (async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_historical_pathway_data.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({ client_id: parseInt(clientId), group_name: "" }),
          }
        );
        const json = await response.json();
        if (json.status && !cancelled) setInitialHistory(json);
      } catch (error) {
        console.error("Fetch initial history failed:", error.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, token]);

  // -- PREVIOUS DATA: the client_id the history panel reads for, resolved from
  // the Review step-1 picker or from the goalreview hand-off in the URL. ----
  const historyClientId = useMemo(() => {
    if (selectedClientId) return parseInt(selectedClientId);
    const fromUrl = urlParams.get("clientId");
    const parsed = parseInt(fromUrl, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }, [selectedClientId, urlParams]);

  // -- PREVIOUS DATA: fetch the answers for one group across every past
  // Initial + Review session of this client. -------------------------------
  const fetchGroupHistory = async (clientId, groupName) => {
    if (!clientId || !token) return;
    setLoadingGroupHistory(true);
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/therapist_api/get_historical_pathway_data.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ client_id: parseInt(clientId), group_name: groupName || "" }),
        }
      );
      const json = await response.json();
      if (json.status) setGroupHistory(json);
      else if (isTokenError(json)) { dispatchSessionExpired(); return; }
    } catch (error) {
      console.error("Fetch group history failed:", error.message);
    } finally {
      setLoadingGroupHistory(false);
    }
  };

  const handleGroupHistoryClick = (groupName) => {
    const next = historyGroupName === groupName ? null : groupName;
    setHistoryGroupName(next);
    if (next && next !== historyGroupName) fetchGroupHistory(historyClientId, next);
  };

  // -- PREVIOUS DATA: "Previous Page" goes back one level WITHIN the view - from
  // an open group's tables to the group file bar. It must NOT call handleBack:
  // handleBack only moves the assessment step, so the click left the therapist
  // trapped in the history view on the same group - and in Review mode on the
  // first question (step 2) it did nothing at all, because handleBack returns
  // early there to avoid dropping into the step-1 setup screen.
  const handleHistoryBack = () => {
    setHistoryGroupName(null);
  };

  // -- PREVIOUS DATA: "X" closes the view entirely and hands the whole
  // assessment area back to the questionnaire. Always available while the pane
  // is open, including when only the group bar is showing, so the full-width
  // pane can never become a dead end.
  const handleCloseHistory = () => {
    setHistoryGroupName(null);
    setShowHistoryFiles(false);
  };

  // -- PREVIOUS DATA: ordered group list for the file-icon bar. Demographics
  // is hoisted to the front because it is the usual entry point. -----------
  const orderedHistoryGroups = useMemo(
    () => orderHistoryGroups(initialHistory, activePathway?.groups),
    [initialHistory, activePathway]
  );

  // -- PREVIOUS DATA: the folder icon on the question-card toggles the panel
  // and opens the first group immediately so it is never an empty view. ----
  const handleHistoryFileClick = () => {
    const willShow = !showHistoryFiles;
    setShowHistoryFiles(willShow);
    if (willShow && orderedHistoryGroups.length > 0) {
      const firstGroup = orderedHistoryGroups[0].group_name;
      setHistoryGroupName(firstGroup);
      if (historyClientId) fetchGroupHistory(historyClientId, firstGroup);
    } else {
      setHistoryGroupName(null);
    }
  };

  // -- PREVIOUS DATA: scroll the inline history into view when it opens. ----
  useEffect(() => {
    if (historyGroupName) {
      historyViewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [historyGroupName]);

  // -- NEW: Map question text ? the answer carried over from the session we
  // inherit from, so review steps can auto-select the same option. HINE / HAI
  // answers carry { value, entries } where entries hold the score rows (only
  // when the inherited answer was "Yes").
  //
  // Data Continuity: the source is the LAST COMPLETED session of any type -
  // once a client has a finished Review, that Review is what the next Review
  // inherits from. The Initial is only the fallback. ------------------------
  // The session being answered is excluded: it is normally the newest row in
  // initialHistory, so without this the "latest session to inherit from" is the
  // current one and nothing is actually inherited.
  const inheritSession = useMemo(
    () => pickInheritanceSession(initialHistory, currentSessionId),
    [initialHistory, currentSessionId]
  );

  // -- Data Continuity: which session this assessment inherits answers from.
  //
  // Plain Review / Initial keep the Data Continuity rule above (the last
  // completed session of any type wins).
  //
  // Review Smart Goal is different by definition: the therapist arrived from a
  // specific row of "Review Existing Goals", so ?goalSessionId names the session
  // that actually owns the goal being reviewed. Its Background Information &
  // Assessments / Current Skills / Comorbidities answers are the ones the goal
  // was written against, and inheriting from a newer unrelated Review instead
  // would silently pre-fill the wrong history.
  //
  // Everything downstream (the three lookup maps and the pre-fill pass itself)
  // reads this one value, so the maps and the answers can never disagree about
  // which session is the source.
  const sourceSession = useMemo(() => {
    if (!isSmartGoalMode || !goalSessionId) return inheritSession;
    const wanted = String(goalSessionId);
    const sessions = Array.isArray(initialHistory?.data) ? initialHistory.data : [];
    return sessions.find(s => String(s.session_id) === wanted && s.appointment_type === 'initial')
      || sessions.find(s => String(s.session_id) === wanted)
      || inheritSession;
  }, [initialHistory, inheritSession, isSmartGoalMode, goalSessionId]);

  const initialAnswerMap = useMemo(() => {
    const map = new Map();
    if (!sourceSession || !Array.isArray(sourceSession.pathway)) return map;
    sourceSession.pathway.forEach(q => {
      if (q.question) map.set(String(q.question).trim().toLowerCase(), q);
    });
    return map;
  }, [sourceSession]);

  // -- Keyword ? inherited answer map (fallback for HINE / HAI steps). --
  // The keyword alone is NOT unique: "Communication" is both a motor-skill
  // question (group 2) and a comorbidity (group 3). Every pathway uses the
  // same keyword for both, so the group index is part of the key. A plain
  // keyword key is kept as a fallback for questions with no usable node.
  const initialKeywordMap = useMemo(() => {
    const map = new Map();
    if (!sourceSession || !Array.isArray(sourceSession.pathway)) return map;
    sourceSession.pathway.forEach(q => {
      if (!q.keyword) return;
      const kw = String(q.keyword).trim().toLowerCase();
      const parts = String(q.node || "").split(".");
      if (parts.length > 1) map.set(parts[1] + "||" + kw, q);
      if (!map.has(kw)) map.set(kw, q);
    });
    return map;
  }, [sourceSession]);

  // -- Normalised-keyword map: third-tier fallback for inherited answers ------
  // Only indexed when the normalised key is unambiguous, so a fuzzy lookup can
  // never move an answer onto the wrong question:
  //   "<group>||<normalised>"  always unique inside a group, so always indexed
  //   "<normalised>"           indexed only when the whole pathway agrees
  // The group prefix is what keeps "Communication skill" (Current Skills) from
  // matching the "Communication" comorbidity, and vice versa.
  const initialFuzzyKeywordMap = useMemo(() => {
    const map = new Map();
    const conflicts = new Set();
    if (!sourceSession || !Array.isArray(sourceSession.pathway)) return map;
    sourceSession.pathway.forEach(q => {
      if (!q.keyword) return;
      const norm = normaliseKeyword(q.keyword);
      if (!norm) return;
      const parts = String(q.node || "").split(".");
      if (parts.length > 1) map.set(parts[1] + "||" + norm, q);
      if (map.has(norm)) conflicts.add(norm); else map.set(norm, q);
    });
    conflicts.forEach(norm => map.delete(norm));
    return map;
  }, [sourceSession]);

  // Resolves an RSG/Review keyword against the source pathway, in tiers:
  //   1. exact keyword, group-scoped then global
  //   2. normalised keyword (filler words dropped), group-scoped then global
  //   3. normalised keyword containment, group-scoped then global
  // Every tier returns null rather than guessing when it would be ambiguous, so
  // an inherited answer is either certainly the right question's or absent.
  const lookupKeyword = (rawKeyword, nodeGroup) => {
    const kw = String(rawKeyword || "").trim().toLowerCase();
    if (!kw) return null;
    if (nodeGroup) {
      const scoped = initialKeywordMap.get(nodeGroup + "||" + kw);
      if (scoped) return scoped;
    }
    const global = initialKeywordMap.get(kw);
    if (global) return global;

    const norm = normaliseKeyword(rawKeyword);
    if (!norm) return null;
    if (nodeGroup) {
      const scopedFuzzy = initialFuzzyKeywordMap.get(nodeGroup + "||" + norm);
      if (scopedFuzzy) return scopedFuzzy;
    }
    const globalFuzzy = initialFuzzyKeywordMap.get(norm);
    if (globalFuzzy) return globalFuzzy;

    // Containment tier, for trailing qualifiers the normaliser cannot drop:
    // "Communication skill" -> "Communication". Restricted to the same group
    // first, and only accepted when it identifies a single question.
    const prefix = nodeGroup ? nodeGroup + "||" : "";
    const candidates = [];
    initialFuzzyKeywordMap.forEach((q, key) => {
      if (prefix && !key.startsWith(prefix)) return;
      const candidate = key.slice(prefix.length);
      if (candidate === norm) return;
      if (candidate.length < MIN_FUZZY_KEYWORD_LEN) return;
      if (norm.includes(candidate) || candidate.includes(norm)) candidates.push(q);
    });
    return candidates.length === 1 ? candidates[0] : null;
  };

  useEffect(() => {
    if (pathwaysData.length === 0) return;
    let needsUpdate = false;
    const newAnswers = { ...answers };
    const nodeMap = {};
    pathwaysData.forEach(pData => {
      if (pData.pathways) {
        pData.pathways.forEach(p => {
          if (p.groups) {
            p.groups.forEach(g => {
              const process = (qs) => {
                qs.forEach(q => { if (q.options) nodeMap[q.node_number] = q.options; });
              };
              process(g.questions);
            });
          }
        });
      }
    });
    Object.keys(newAnswers).forEach(node => {
      const ans = newAnswers[node];
      if (Array.isArray(ans) && ans.length > 0 && typeof ans[0] === "number") {
        const options = nodeMap[node];
        if (options) {
          const texts = ans.map(id => { const opt = options.find(o => o.option_id === id); return opt ? opt.option_text : id; }).filter(t => typeof t === "string");
          if (texts.length > 0) { newAnswers[node] = texts; needsUpdate = true; }
        }
      } else if (typeof ans === "number") {
        const options = nodeMap[node];
        if (options) { const opt = options.find(o => o.option_id === ans); if (opt) { newAnswers[node] = opt.option_text; needsUpdate = true; } }
      }
    });
    if (needsUpdate) setAnswers(newAnswers);
    // answers is intentionally omitted: this only normalises legacy option-id
    // rows on load, and re-running on every answer change would rewrite state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathwaysData]);

  // -- Drop an age group the current appointment cannot run -------------------
  // Review and Review Smart Goal have separate pathways, so an age group that
  // one of them offers is not necessarily offered by the other. The selected
  // value can arrive from several places that know nothing about the active
  // appointment - getReviewAgeForClient's Initial fallback, ?age= from
  // goalreview, a restored draft - and switching the step-1 appointment radio
  // leaves the previous value in place too.
  //
  // A value that is not in the active list is cleared instead of being shown,
  // so the <select> can never display an option it does not offer and the
  // therapist is forced to pick a real one before Next. Skipped while the
  // pathway list is still loading or empty, so a valid value is never wiped
  // during the initial fetch.
  useEffect(() => {
    if (!isReviewMode) return;
    if (!ageRange) return;
    if (reviewAgeGroups.length === 0) return;
    if (!reviewAgeGroups.includes(ageRange)) setAgeRange("");
  }, [isReviewMode, ageRange, reviewAgeGroups]);

  // -- Set active pathway when ageRange changes ------------------------------
  useEffect(() => {
    if (ageRange && pathwaysData.length > 0) {
      const selectedAgeObj = pathwaysData.find(p => p.age_group === ageRange);
      if (selectedAgeObj && selectedAgeObj.pathways && selectedAgeObj.pathways.length > 0) {
        // Review Smart Goal has its own `review smart goal` pathway, so it is
        // looked up first and falls back to the plain `review` pathway when the
        // admin has not created one for this age group yet. Plain Review does
        // the mirror image. Keep this order in sync with $pathwayTypes in
        // therapist_api/save_basic_details.php so the clpa_id stored on the
        // session matches the pathway rendered on screen.
        const desiredTypes = isSmartGoalMode
          ? ["review smart goal", "review"]
          : isReviewMode
            ? ["review", "review smart goal"]
            : ["initial"];
        const pathway =
          desiredTypes
            .map(t => selectedAgeObj.pathways.find(p => (p.appointment_type || "").toLowerCase() === t))
            .find(Boolean) ||
          selectedAgeObj.pathways[0];
        setActivePathway(pathway);
      } else {
        setActivePathway(null);
      }
    }
  }, [ageRange, pathwaysData, session, isReviewMode, isSmartGoalMode]);

  // -- Fetch interventions ---------------------------------------------------
  const fetchInterventions = async () => {
    if (!currentSessionId) return;

    setInterventionsData(null);
    setActiveGoalTab("");
    setAccordionOpen({});

    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/therapist_api/get_test_intervention.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_id: parseInt(currentSessionId),
            // Review Smart Goal: the catalogue keys inherit the domains/skills
            // from the session that owns the goal being reviewed, so tell the
            // backend which one that is.
            ...(isSmartGoalMode && goalSessionId ? { goal_session_id: parseInt(goalSessionId) } : {}),
          }),
        }
      );

      const json = await response.json();

      if (json.status) {
        setInterventionsData(json);

        const goalTabs = Object.keys(json.data || {}).map((key) => {
          const items = json.data[key] || [];
          const firstItem = items[0] || {};

          const goalDomain = firstItem.goal_domain || "";
          const goalSkill = firstItem.goal_skill || "";

          const tabKey =
            goalSkill && goalSkill !== "null"
              ? goalSkill
              : goalDomain;

          const displayLabel =
            goalSkill && goalSkill !== "null"
              ? `${goalDomain} - ${goalSkill}`
              : goalDomain;

          return {
            key,
            tabKey,
            goalDomain,
            goalSkill,
            displayLabel,
          };
        });

        const uniqueGoalTabs = goalTabs.filter(
          (tab, index, self) =>
            index ===
            self.findIndex((t) => t.tabKey === tab.tabKey)
        );

        if (uniqueGoalTabs.length > 0) {
          const firstTab = uniqueGoalTabs[0];

          setActiveGoalTab(firstTab.tabKey);

          const firstTabInterventions =
            json.data[firstTab.key] || [];

          if (firstTabInterventions.length > 0) {
            const initialOpen = {};
            initialOpen[`int-${firstTabInterventions[0].id}`] = true;
            if (firstTabInterventions.length > 1) {
              initialOpen[`int-${firstTabInterventions[1].id}`] = true;
            }
            setAccordionOpen(initialOpen);
          }
        }
      } else {
        if (isTokenError(json)) { dispatchSessionExpired(); return; }
      }
    } catch (error) {
      console.error(
        "Error fetching interventions:",
        error.message
      );
    }
  };

  // -- Derived: flat list of all questions ----------------------------------
  const allQuestionsFlat = useMemo(() => {
    const steps = [];
    if (activePathway && activePathway.groups) {
      activePathway.groups.forEach(group => {
        if (group.group_name.toLowerCase() === "demographics") return;

        function processSubOptionsForFlat(subOptions) {
          if (!subOptions || subOptions.length === 0) return;
          subOptions.forEach(subOpt => {
            if (subOpt.sub_options && subOpt.sub_options.length > 0) {
              processSubOptionsForFlat(subOpt.sub_options);
            }
            if (subOpt.sub_questions && subOpt.sub_questions.length > 0) {
              processOptions(subOpt.sub_questions);
            }
          });
        }

        function processOptions(questions) {
          questions.forEach(q => {
            steps.push({ ...q, groupName: group.group_name, groupNode: group.node_number, groupInfo: group.group_info });
            if (q.options && q.options.length > 0) {
              q.options.forEach(opt => {
                if (opt.sub_options && opt.sub_options.length > 0) {
                  processSubOptionsForFlat(opt.sub_options);
                }
                if (opt.sub_questions && opt.sub_questions.length > 0) processOptions(opt.sub_questions);
              });
            }
          });
        }

        processOptions(group.questions);
      });
    }
    return steps;
  }, [activePathway]);

  const groupNodeSet = useMemo(() => {
    const set = new Set();
    if (activePathway && activePathway.groups) activePathway.groups.forEach(g => set.add(g.node_number));
    return set;
  }, [activePathway]);

  const pathwayHasEndInstruction = useMemo(
    () => hasEndInstructionDeep(activePathway),
    [activePathway]
  );

  const visibleSteps = useMemo(() => {
    const selectedOptionNodes = new Set();

    function processSubOptionsChainForVisible(qNode, pathTexts, subOptions) {
      if (!subOptions || subOptions.length === 0) return;
      const chainKey = [qNode, ...pathTexts, "sub"].join("_");
      const chainAns = answers[chainKey];
      if (!chainAns) return;
      subOptions.forEach((subOpt) => {
        const isSelected = Array.isArray(chainAns) ? chainAns.includes(subOpt.option_text) : chainAns === subOpt.option_text;
        if (!isSelected) return;
        selectedOptionNodes.add(subOpt.node_number);
        if (subOpt.sub_options && subOpt.sub_options.length > 0) {
          processSubOptionsChainForVisible(qNode, [...pathTexts, subOpt.option_text], subOpt.sub_options);
        }
        if (subOpt.sub_questions) processQuestions(subOpt.sub_questions);
      });
    }

    function processQuestions(questions) {
      if (!questions) return;
      questions.forEach((q) => {
        const ans = answers[q.node_number];
        if (!ans || !q.options) return;
        q.options.forEach((opt) => {
          const isSelected = Array.isArray(ans) ? ans.includes(opt.option_text) : ans === opt.option_text;
          if (!isSelected) return;
          selectedOptionNodes.add(opt.node_number);
          if (opt.sub_options && opt.sub_options.length > 0) {
            processSubOptionsChainForVisible(q.node_number, [opt.option_text], opt.sub_options);
          }
          if (opt.sub_questions) processQuestions(opt.sub_questions);
        });
      });
    }

    processQuestions(allQuestionsFlat);

    return allQuestionsFlat.filter((q) => {
      if (groupNodeSet.has(q.parent_node)) return true;
      if (selectedOptionNodes.has(q.parent_node)) return true;
      return false;
    });
  }, [allQuestionsFlat, answers, groupNodeSet]);

  // -- NEW: Pre-fill the review questionnaire with the already-recorded
  // data from the session we inherit from ? auto-select the SAME option,
  // carry over the HINE/HAI score entries, and select sub-options too.
  // Runs once per client.
  const prefilledClientRef = useRef(null);
  const prefilledDoneRef = useRef(false);
  useEffect(() => {
    const clientId = selectedClientId;
    if (prefilledClientRef.current !== clientId) {
      prefilledClientRef.current = clientId;
      prefilledDoneRef.current = false;
    }
    if (!clientId || !currentSessionId || initialHistory === null || visibleSteps.length === 0) return;
    // Both review modes land on step 2 = the 4th question overall (Q1-Q3 are the
    // step-1 setup answers). Prefill from step 2 so Background Information &
    // Assessments -> Comorbidities are already answered by the time the therapist
    // reaches them. The Initial flow keeps the original step-3 timing.
    const minStep = isReviewMode ? 2 : 3;
    if (step < minStep) return;
    if (prefilledDoneRef.current) return;
    prefilledDoneRef.current = true;

    // For Review Smart Goal the source is the session that owns the goal
    // (the one named by ?goalSessionId), because that is the pathway the
    // therapist clicked through from "Review Existing Goals" - not merely the
    // latest completed session. sourceSession is the single place that picks.
    if (!sourceSession || !Array.isArray(sourceSession.pathway)) return;

    const newAnswers = {};
    const hineRows = [];
    const haiRows = [];
    // Only used by Review Smart Goal: the pre-filled answers are written back
    // to the review session so the record is complete even if the therapist
    // only changes one question.
    const autoSaveResponses = [];

    // Walk the initial option tree and re-build the same chain keys that
    // renderSubOptionsChain / handleChainSubOptionSelect use, so the
    // selected sub-options show up inline on the review question.
    const fillChain = (qNode, initialOptions, ancestors) => {
      if (!Array.isArray(initialOptions)) return;
      initialOptions.forEach(opt => {
        if (!opt || !opt.selected) return;
        const children = Array.isArray(opt.suboptions) ? opt.suboptions : [];
        const selectedChildren = children.filter(c => c && c.selected);
        if (selectedChildren.length > 0) {
          const subKey = [qNode, ...ancestors, opt.label, "sub"].join("_");
          newAnswers[subKey] = selectedChildren.length === 1
            ? (selectedChildren[0].answer ?? selectedChildren[0].label)
            : selectedChildren.map(c => (c.answer ?? c.label));
        }
        selectedChildren.forEach(child => {
          if (child.suboptions && child.suboptions.length > 0) {
            fillChain(qNode, [child], [...ancestors, opt.label]);
          }
        });
      });
    };

    allQuestionsFlat.forEach(q => {
      let match = q.question_text
        ? initialAnswerMap.get(String(q.question_text).trim().toLowerCase()) || null
        : null;
      if (!match && q.keywords) {
        const nodeGroup = String(q.node_number || "").split(".")[1] || "";
        match = lookupKeyword(q.keywords, nodeGroup);
      }
      if (!match) return;
      const grp = (q.groupName || "").toLowerCase();
      const isHineHai = match.type === "HINE" || match.type === "HAI";
      // Groups whose questions carry over from the source session.
      //
      // "goals" is included so the new-goal questions are pre-filled too. A
      // Review asks "What is the domain of the new goal?" and "Have you
      // identified any goal limiting factors?" in its Goals group, and the
      // source session asks the same two under the SAME keywords ("Goal Domain"
      // / "Goal Limiting Factors"), so the keyword lookup already resolves them.
      // Excluding the group left both questions blank on every new Review, so
      // the therapist had to re-enter a domain the client already had, and the
      // summary page then had no goal to show.
      //
      // "goal progression" stays excluded: those questions are about THIS
      // review ("current goal status", "factors delaying progress") and have no
      // counterpart in the source session, so they must be answered fresh.
      const inheritGroups = ["background", "current skills", "comorbid", "goals"];
      if (!isHineHai && !inheritGroups.some(g => grp.includes(g))) return;

      if (isHineHai) {
        if (answers[q.node_number] !== undefined) return;
        const value = match.answer?.value;
        if (value) newAnswers[q.node_number] = value;
        const entries = Array.isArray(match.answer?.entries) ? match.answer.entries : [];
        if (String(value || "").toLowerCase() === "yes" && entries.length > 0) {
          if (match.type === "HINE") {
            entries.forEach(e => hineRows.push({
              id: Date.now() + Math.random(),
              timepoint: e.timepoint || "",
              hineScore: e.hine_score ?? 0,
              rightAsym: e.right_asymmetry ?? 0,
              leftAsym: e.left_asymmetry ?? 0,
            }));
            } else {
              entries.forEach(e => haiRows.push({
                id: Date.now() + Math.random(),
                timepoint: e.timepoint || "",
                rightEahs: e.right_score ?? 0,
                leftEahs: e.left_score ?? 0,
                eachHandSumScore: e.sum ?? 0,
                asymmetryIndex: e.asymmetry ?? 0,
              }));
            }
          }
          if (isReviewMode) {
            if (String(value || "").toLowerCase() === "yes" && entries.length > 0) {
              autoSaveResponses.push({
                node: q.node_number,
                entity: "question",
                entityId: q.question_id || 0,
                answer: value,
                type: match.type.toLowerCase(),
                entries: entries,
              });
            } else {
              autoSaveResponses.push({
                node: q.node_number,
                entity: "question",
                entityId: q.question_id || 0,
                answer: value || "No",
                type: match.type.toLowerCase(),
              });
            }
          }
        } else {
          const ans = match.answer;
          if (ans !== null && ans !== undefined && ans !== "" && answers[q.node_number] === undefined) {
            // The history API's `answer` for an answered option tree is the
            // FLATTENED list of every selected label - the top-level option and
            // its sub-options (e.g. ["Yes","Unilateral"]). Putting the whole
            // array into the main answer makes the step list resolve the
            // sub-option text a second time ("Unilateral, Unilateral") and
            // writes a mangled "Yes,Unilateral" string into the review session.
            // Only the top-level selected option(s) belong in the main answer;
            // sub-options are carried via the `_sub` chain keys fillChain()
            // writes below.
            let mainAnswer = ans;
            const topLevelSelected = Array.isArray(match.options)
              ? match.options.filter(o => o && o.selected).map(o => o.label)
              : [];
            if (topLevelSelected.length > 0) {
              mainAnswer = topLevelSelected.length === 1 ? topLevelSelected[0] : topLevelSelected;
            }
            if (mainAnswer !== null && mainAnswer !== undefined && mainAnswer !== "") {
              newAnswers[q.node_number] = mainAnswer;
              if (isReviewMode) {
                const selectedOpt = (match.options || []).find(
                  o => String(o.label || "").toLowerCase() === String(mainAnswer || "").toLowerCase()
                );
                autoSaveResponses.push({
                  node: q.node_number,
                  entity: "option",
                  entityId: selectedOpt?.option_id || q.question_id || 0,
                  answer: String(mainAnswer),
                });
              }
            }
          }
          fillChain(q.node_number, match.options, []);
        }
      });

    setAnswers(prev => {
      const merged = { ...prev };
      let changed = false;
      Object.keys(newAnswers).forEach(k => {
        if (merged[k] === undefined) { merged[k] = newAnswers[k]; changed = true; }
      });
      return changed ? merged : prev;
    });
    const hineEmpty = hineData.length === 0 || (hineData.length === 1 && !hineData[0].timepoint && !hineData[0].hineScore);
    const haiEmpty = haiData.length === 0 || (haiData.length === 1 && !haiData[0].timepoint && !haiData[0].rightEahs);
    if (hineRows.length > 0 && hineEmpty) setHineData(hineRows);
    if (haiRows.length > 0 && haiEmpty) setHaiData(haiRows);

    if (isReviewMode && autoSaveResponses.length > 0) {
      fetchWithTimeout(
        `${API_BASE}/therapist_api/save_review_auto_responses.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ sessionId: parseInt(currentSessionId), responses: autoSaveResponses }),
        }
      )
        .then((res) => res.json())
        .then((json) => {
          if (!json.status && isTokenError(json)) dispatchSessionExpired();
        })
        .catch((error) => console.error("Failed to auto-save pre-filled review answers:", error.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId, step, allQuestionsFlat, visibleSteps, sourceSession, answers, hineData, haiData, selectedClientId, isReviewMode, token]);

  // -- Review Smart Goal: load the ORIGINATING pathway's Goal Domains / Goals
  // so summ-container1 can show what is being reviewed and revised. ---------
  useEffect(() => {
    if (!isSmartGoalMode) { setSmartGoalContext(null); return; }
    if (!token) return;
    if (!goalSessionId && !selectedClientId) return;

    let cancelled = false;
    setSmartGoalLoading(true);
    (async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_smart_goal_context.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({
              goal_session_id: goalSessionId ? parseInt(goalSessionId) : 0,
              client_id: selectedClientId ? parseInt(selectedClientId) : 0,
            }),
          }
        );
        const json = await response.json();
        if (cancelled) return;
        if (json.status) {
          setSmartGoalContext(json);
          // Adopt the originating session as the goal context when the entry
          // point did not carry one explicitly.
          if (!goalSessionId && json.source?.sessionId) setGoalSessionId(String(json.source.sessionId));
        } else if (isTokenError(json)) {
          dispatchSessionExpired();
        }
      } catch (error) {
        if (!cancelled) console.error("Failed to load Review Smart Goal context:", error.message);
      } finally {
        if (!cancelled) setSmartGoalLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmartGoalMode, goalSessionId, selectedClientId, token]);

  // -- DRAFT RESTORE: calculate step from last_node once visibleSteps ready -
  useEffect(() => {
    const targetNode = targetNodeRef.current;
    if (!targetNode || visibleSteps.length === 0 || isRestoringDraft) return;

    console.log("?? Resolving draft last_node:", targetNode, "against", visibleSteps.length, "visible steps");

    const targetIndex = visibleSteps.findIndex(q => q.node_number === targetNode);

    if (targetIndex !== -1) {
      const calculatedStep = targetIndex + 2;
      setStep(calculatedStep);
      const targetQuestion = visibleSteps[targetIndex];
      if (targetQuestion && targetQuestion.groupName) {
        setOpenSections(prev => ({ ...prev, [targetQuestion.groupName]: true }));
      }
      console.log("? Draft restored to step:", calculatedStep, "node:", targetNode);
    } else {
      const lastIndex = visibleSteps.length - 1;
      if (lastIndex >= 0) {
        setStep(lastIndex + 2);
        const lastQ = visibleSteps[lastIndex];
        if (lastQ && lastQ.groupName) {
          setOpenSections(prev => ({ ...prev, [lastQ.groupName]: true }));
        }
        console.warn("?? last_node not found in visibleSteps. Defaulted to last step:", lastIndex + 2);
      }
    }

    targetNodeRef.current = null;
  }, [visibleSteps, isRestoringDraft]);

  // -- LOCAL RESUME: restore step once visibleSteps are ready ----------------
  useEffect(() => {
    const savedStep = localResumeStepRef.current;
    if (!savedStep || visibleSteps.length === 0 || isRestoringDraft) return;
    const targetIndex = savedStep - 2;
    if (targetIndex >= 0 && targetIndex < visibleSteps.length) {
      setStep(savedStep);
      const targetQ = visibleSteps[targetIndex];
      if (targetQ && targetQ.groupName) setOpenSections(prev => ({ ...prev, [targetQ.groupName]: true }));
      console.log("? Resumed to step:", savedStep, "node:", targetQ?.node_number);
    } else {
      const lastIndex = visibleSteps.length - 1;
      setStep(lastIndex + 2);
      console.warn("?? Saved step out of range, defaulted to last step:", lastIndex + 2);
    }
    localResumeStepRef.current = null;
  }, [visibleSteps, isRestoringDraft]);

  // -- Auto-open section when navigating normally ----------------------------
  useEffect(() => {
    if (targetNodeRef.current) return;
    if (step >= 2 && visibleSteps[step - 2]) {
      const currentQ = visibleSteps[step - 2];
      if (currentQ.groupName) {
        setOpenSections(prev => ({ ...prev, [currentQ.groupName]: true }));
      }
    }
  }, [step, visibleSteps]);

  // -- Auto-scroll to active step --------------------------------------------
  useEffect(() => {
    if (activeStepRef.current) {
      activeStepRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [step, visibleSteps]);

  // -- Trigger interventions fetch on final step -----------------------------
  // Keyed on step/session/questions only: fetchInterventions is a stable
  // wrapper and re-running on every `answers` change would refetch per click.
  useEffect(() => {
    const totalSteps = 1 + (visibleSteps ? visibleSteps.length : 0);
    if (step === totalSteps + 1 && currentSessionId) fetchInterventions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, currentSessionId, visibleSteps, answers]);

  // -- Initialize accordion when interventions load --------------------------
  useEffect(() => {
    if (interventionsData && interventionsData.data && activeGoalTab && Object.keys(accordionOpen).length === 0) {
      const list = interventionsData.data[activeGoalTab];
      if (list && list.length > 0) {
        const initialOpen = {};
        initialOpen[`int-${list[0].id}`] = true;
        if (list.length > 1) {
          initialOpen[`int-${list[1].id}`] = true;
        }
        setAccordionOpen(initialOpen);
      }
    }
    // accordionOpen is read as a guard, not tracked: including it would make
    // the effect self-cancelling (it sets accordionOpen itself).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interventionsData, activeGoalTab]);

  // ---------------------------------------------------------------------------
  // ANSWER HELPERS
  // ---------------------------------------------------------------------------

  const clearDownstreamAnswers = (prevAnswers, currentNodeNumber) => {
    const newAnswers = { ...prevAnswers };
    const currentIndex = visibleSteps.findIndex(q => q.node_number === currentNodeNumber);
    if (currentIndex !== -1) {
      for (let i = currentIndex + 1; i < visibleSteps.length; i++) {
        const futureQ = visibleSteps[i];
        delete newAnswers[futureQ.node_number];
        delete newAnswers[`${futureQ.node_number}_sub`];
        Object.keys(prevAnswers).forEach(key => {
          if (key.startsWith(`${futureQ.node_number}_`) && key !== `${futureQ.node_number}_sub`) delete newAnswers[key];
        });
      }
    }
    return newAnswers;
  };

  const EATING_AND_DRINKING_OPTION = "Eating and Drinking";

  const handleMainOptionSelect = (qNode, opt, isMulti = false, isGoalDomain = false) => {
    // -- "Eating and Drinking" can only be selected as a single choice in the
    // Goal Domain question: block (with a popup) any combination of it with
    // other domains. ---------------------------------------------------------
    if (isGoalDomain) {
      const currentSelections = Array.isArray(answers[qNode]) ? answers[qNode] : [];
      const isEatingDrinking = opt.option_text === EATING_AND_DRINKING_OPTION;
      if (!currentSelections.includes(opt.option_text)) {
        if ((isEatingDrinking && currentSelections.length > 0) ||
          (!isEatingDrinking && currentSelections.includes(EATING_AND_DRINKING_OPTION))) {
          setShowGoalDomainPopup(true);
          return false;
        }
      }
    }
    setAnswers(prev => {
      let newAnswers = { ...prev };
      if (!isMulti) {
        Object.keys(prev).forEach(key => { if (key.startsWith(`${qNode}_`)) delete newAnswers[key]; });
        newAnswers[qNode] = opt.option_text;
      } else {
        // A multi-select question can hold a legacy single-string value (e.g.
        // saved while the question was still single-select). Treat a string as
        // a one-element selection so it is preserved, not dropped, when the
        // first additional option is ticked.
        const currentSelections = Array.isArray(prev[qNode])
          ? [...prev[qNode]]
          : (typeof prev[qNode] === "string" && prev[qNode] ? [prev[qNode]] : []);
        if (currentSelections.includes(opt.option_text)) {
          newAnswers[qNode] = currentSelections.filter(s => s !== opt.option_text);
          delete newAnswers[`${qNode}_${opt.option_text}_sub`];
        } else {
          if (isGoalDomain && currentSelections.length >= 2) {
            return prev;
          }
          newAnswers[qNode] = [...currentSelections, opt.option_text];
        }
      }
      newAnswers = clearDownstreamAnswers(newAnswers, qNode);
      return newAnswers;
    });
    return true;
  };

  const handleSubOptionSelect = (qNode, parentOptText, subOpt, isMulti = false, limit = 1) => {
    setAnswers(prev => {
      let newAnswers = { ...prev };
      const key = `${qNode}_${parentOptText}_sub`;
      if (isMulti) {
        let currentSelections = Array.isArray(prev[key]) ? [...prev[key]] : [];
        if (currentSelections.includes(subOpt.option_text)) {
          newAnswers[key] = currentSelections.filter(s => s !== subOpt.option_text);
        } else {
          if (currentSelections.length < limit) newAnswers[key] = [...currentSelections, subOpt.option_text];
        }
      } else { newAnswers[key] = subOpt.option_text; }
      newAnswers = clearDownstreamAnswers(newAnswers, qNode);
      return newAnswers;
    });
  };

  const handleChainSubOptionSelect = (qNode, pathTexts, subOpt, isMulti = false, limit = 1) => {
    setAnswers(prev => {
      let newAnswers = { ...prev };
      const key = [qNode, ...pathTexts, "sub"].join("_");
      if (isMulti) {
        let currentSelections = Array.isArray(prev[key]) ? [...prev[key]] : [];
        if (currentSelections.includes(subOpt.option_text)) {
          newAnswers[key] = currentSelections.filter(s => s !== subOpt.option_text);
        } else {
          if (currentSelections.length < limit) newAnswers[key] = [...currentSelections, subOpt.option_text];
        }
      } else {
        newAnswers[key] = subOpt.option_text;
      }
      newAnswers = clearDownstreamAnswers(newAnswers, qNode);
      return newAnswers;
    });
  };

  // ---------------------------------------------------------------------------
  // HINE HANDLERS
  // ---------------------------------------------------------------------------

  // Typing allows digits with ONE decimal point (e.g., 12.3)
  const sanitizeDecimalText = (value) => {
    let s = String(value).replace(/[^0-9.]/g, "");
    const dot = s.indexOf(".");
    if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
    return s.replace(/^0+(?=[0-9])/, "");
  };

  // Clear the default "0" on focus so the user can type directly; restore "0" on blur if left empty
  const clearZeroOnFocusHine = (e, index, field) => {
    if (["hineScore", "rightAsym", "leftAsym"].includes(field)) {
      const cur = hineData[index]?.[field];
      if (cur === 0 || cur === "0" || cur === "" || cur == null) {
        handleHineChange(index, field, "");
      }
    }
  };
  const restoreZeroOnBlurHine = (e, index, field) => {
    if (["hineScore", "rightAsym", "leftAsym"].includes(field) && (hineData[index]?.[field] === "" || hineData[index]?.[field] == null)) {
      handleHineChange(index, field, "0");
    }
  };
  // Same for HAI (Each-hand + Asymmetry) fields
  const clearZeroOnFocusHai = (e, index, field) => {
    if (["rightEahs", "leftEahs", "eachHandSumScore", "asymmetryIndex"].includes(field)) {
      const cur = haiData[index]?.[field];
      if (cur === 0 || cur === "0" || cur === "" || cur == null) {
        handleHaiChange(index, field, "");
      }
    }
  };
  const restoreZeroOnBlurHai = (e, index, field) => {
    if (["rightEahs", "leftEahs", "eachHandSumScore", "asymmetryIndex"].includes(field) && (haiData[index]?.[field] === "" || haiData[index]?.[field] == null)) {
      handleHaiChange(index, field, "0");
    }
  };

  const handleHineChange = (index, field, value) => {
    const updated = [...hineData];
    if (["hineScore", "rightAsym", "leftAsym"].includes(field)) {
      updated[index][field] = sanitizeDecimalText(value);
    } else { updated[index][field] = value; }
    setHineData(updated);
    const hineQuestion = visibleSteps.find(q => getQuestionType(q).isHINE);
    if (hineQuestion && answers[hineQuestion.node_number] === "Yes") {
      saveHineHaiAPI(currentSessionId, hineQuestion.node_number, "hine", hineQuestion.question_id, "Yes",
        updated.map(row => ({ timepoint: row.timepoint, hine_score: Number(row.hineScore) || 0, right_asymmetry: Number(row.rightAsym) || 0, left_asymmetry: Number(row.leftAsym) || 0 })));
    }
  };
  // + / - steps ONLY the whole-number (left) part; decimal part stays unchanged (12.3 -> 13.3)
  const handleHineIncrement = (index, field) => { const cur = parseFloat(hineData[index]?.[field]) || 0; const intPart = Math.trunc(cur); const frac = Math.round((cur - intPart) * 100) / 100; handleHineChange(index, field, String(intPart + 1 + frac)); };
  const handleHineDecrement = (index, field) => { const cur = parseFloat(hineData[index]?.[field]) || 0; const intPart = Math.trunc(cur); if (intPart > 0) { const frac = Math.round((cur - intPart) * 100) / 100; handleHineChange(index, field, String(intPart - 1 + frac)); } };
  const addHineRow = () => {
    setHineData(prev => {
      const newRows = [...prev, { id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }];
      const hineQuestion = visibleSteps.find(q => getQuestionType(q).isHINE);
      if (hineQuestion && answers[hineQuestion.node_number] === "Yes")
        saveHineHaiAPI(currentSessionId, hineQuestion.node_number, "hine", hineQuestion.question_id, "Yes",
          newRows.map(row => ({ timepoint: row.timepoint, hine_score: Number(row.hineScore) || 0, right_asymmetry: Number(row.rightAsym) || 0, left_asymmetry: Number(row.leftAsym) || 0 })));
      return newRows;
    });
  };
  const removeHineRow = (index) => {
    setHineData(prev => {
      const newRows = prev.filter((_, i) => i !== index);
      const hineQuestion = visibleSteps.find(q => getQuestionType(q).isHINE);
      if (hineQuestion && answers[hineQuestion.node_number] === "Yes")
        saveHineHaiAPI(currentSessionId, hineQuestion.node_number, "hine", hineQuestion.question_id, "Yes",
          newRows.map(row => ({ timepoint: row.timepoint, hine_score: Number(row.hineScore) || 0, right_asymmetry: Number(row.rightAsym) || 0, left_asymmetry: Number(row.leftAsym) || 0 })));
      return newRows;
    });
  };

  // ---------------------------------------------------------------------------
  // HAI HANDLERS
  // ---------------------------------------------------------------------------

  const handleHaiChange = (index, field, value) => {
    const updated = [...haiData];
    if (["rightEahs", "leftEahs", "asymmetryIndex", "eachHandSumScore"].includes(field)) {
      updated[index][field] = sanitizeDecimalText(value);
    } else { updated[index][field] = value; }
    setHaiData(updated);
    const haiQuestion = visibleSteps.find(q => getQuestionType(q).isHAI);
    if (haiQuestion && answers[haiQuestion.node_number] === "Yes") {
      saveHineHaiAPI(currentSessionId, haiQuestion.node_number, "hai", haiQuestion.question_id, "Yes",
        updated.map(row => ({ timepoint: row.timepoint, right_score: Number(row.rightEahs) || 0, left_score: Number(row.leftEahs) || 0, sum: Number(row.eachHandSumScore) || 0, asymmetry: Number(row.asymmetryIndex) || 0 })));
    }
  };
  // + / - steps ONLY the whole-number (left) part; decimal part stays unchanged (12.3 -> 13.3)
  const handleHaiIncrement = (index, field) => { const cur = parseFloat(haiData[index]?.[field]) || 0; const intPart = Math.trunc(cur); const frac = Math.round((cur - intPart) * 100) / 100; handleHaiChange(index, field, String(intPart + 1 + frac)); };
  const handleHaiDecrement = (index, field) => { const cur = parseFloat(haiData[index]?.[field]) || 0; const intPart = Math.trunc(cur); if (intPart > 0) { const frac = Math.round((cur - intPart) * 100) / 100; handleHaiChange(index, field, String(intPart - 1 + frac)); } };
  const addHaiRow = () => {
    setHaiData(prev => {
      const newRows = [...prev, { id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }];
      const haiQuestion = visibleSteps.find(q => getQuestionType(q).isHAI);
      if (haiQuestion && answers[haiQuestion.node_number] === "Yes")
        saveHineHaiAPI(currentSessionId, haiQuestion.node_number, "hai", haiQuestion.question_id, "Yes",
          newRows.map(row => ({ timepoint: row.timepoint, right_score: Number(row.rightEahs) || 0, left_score: Number(row.leftEahs) || 0, sum: Number(row.eachHandSumScore) || 0, asymmetry: Number(row.asymmetryIndex) || 0 })));
      return newRows;
    });
  };
  const removeHaiRow = (index) => {
    setHaiData(prev => {
      const newRows = prev.filter((_, i) => i !== index);
      const haiQuestion = visibleSteps.find(q => getQuestionType(q).isHAI);
      if (haiQuestion && answers[haiQuestion.node_number] === "Yes")
        saveHineHaiAPI(currentSessionId, haiQuestion.node_number, "hai", haiQuestion.question_id, "Yes",
          newRows.map(row => ({ timepoint: row.timepoint, right_score: Number(row.rightEahs) || 0, left_score: Number(row.leftEahs) || 0, sum: Number(row.eachHandSumScore) || 0, asymmetry: Number(row.asymmetryIndex) || 0 })));
      return newRows;
    });
  };

  // ---------------------------------------------------------------------------
  // MISC HELPERS
  // ---------------------------------------------------------------------------

  const getContinuousNumber = (targetQuestion) => {
    const targetIndex = visibleSteps.findIndex(q => q.node_number === targetQuestion.node_number);
    return 3 + targetIndex + 1;
  };

  // Left-panel keyword text for a question.
  // The Goal Domain question is the ONLY one that used to get a special label
  // ("Goals - <skills>"), which both renamed the keyword and moved the skills
  // in front of the answer value. Combined with the row's own " - " separator
  // that produced "Goals - Sitting - Sitting, Play and Cognition" - the domain
  // ("Motor") was lost entirely. The keyword is now shown as stored
  // ("Goal Domain") and the skills are rendered as part of the answer instead,
  // so the row reads "Goal Domain - Motor - Sitting, Play and Cognition".
  const getPanelKeywordLabel = (q) => {
    if (q.keywords) return q.keywords;
    return isGoalDomainQuestion(q) ? "Goal Domain" : "Keywords";
  };

  const getAnswerDisplayText = (q) => {
    const mainAns = answers[q.node_number];
    if (mainAns === undefined || mainAns === null || mainAns === "") return "";
    const mainAnsArr = Array.isArray(mainAns) ? mainAns : [mainAns];
    const parts = [];
    const isGoalDomain = isGoalDomainQuestion(q);
    mainAnsArr.forEach(ansText => {
      const selectedOpt = q.options?.find(o => o.option_text === ansText);
      const hasSubOptions = selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0;
      // "Motor-Sitting" style values arrive glued from a few older paths.
      const domain = cleanGoalDomainText(ansText);
      if (hasSubOptions) {
        const domainKey = `${q.node_number}_${ansText}_sub`;
        const subAns = answers[domainKey];
        const stdSubAns = answers[`${q.node_number}_sub`];
        const rawSub = subAns || stdSubAns;
        const subArr = rawSub == null || rawSub === "" ? [] : (Array.isArray(rawSub) ? rawSub : [rawSub]);
        if (subArr.length > 0) {
          if (isGoalDomain) {
            // Goal Domain: keep the domain in front of its own skill, otherwise a
            // skill shared by two domains (or a domain with no skill list) is
            // impossible to tell apart - "Sitting, Play and Cognition" says
            // nothing about what "Sitting" belongs to.
            subArr.forEach(s => {
              const skill = String(s || "").trim();
              if (skill) parts.push(`${domain} - ${skill}`);
            });
          } else {
            const subText = subArr.map(s => String(s || "").trim()).filter(Boolean).join(", ");
            if (subText) parts.push(subText);
            else parts.push(ansText);
          }
        } else {
          parts.push(isGoalDomain ? domain : ansText);
        }
      } else {
        parts.push(isGoalDomain ? domain : ansText);
      }
    });
    // Guard: a part can be resolved both from a sub-option chain AND from the
    // raw main-answer element (older data held the flattened ["Yes","Unilateral"]
    // array / "Yes,Unilateral" string). Collapse so the label never repeats.
    return Array.from(new Set(parts)).join(", ");
  };

  // -- Initial can only be performed ONCE per client. Asks the server before
  // creating anything so the therapist gets an actionable message instead of a
  // duplicate client/session. save_basic_details.php enforces the same rule
  // server-side, so this is a fast-fail convenience rather than the guard.
  const checkInitialEligibility = async (clientName, ageGroup) => {
    const response = await fetchWithTimeout(
      `${API_BASE}/therapist_api/check_initial_eligibility.php`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ appointment_type: "initial", client_name: clientName, age_group: ageGroup }),
      }
    );
    return response.json();
  };

  // -- Step 1 "Next" for every appointment type.
  // Single API for all three: save_basic_details.php. It is the same endpoint
  // the Initial flow has always used, and it now also handles the review
  // appointment types by accepting client_id (reuse the existing client instead
  // of inserting a duplicate), resuming an in-progress session for the same
  // client + appointment type, and persisting Q1-Q3 for review sessions.
  // Returns the parsed JSON on success, or null (with the error already shown).
  const handleSaveBasicDetails = async () => {
    if (!token || !session || !client || !ageRange) return null;

    // Both review modes run against an EXISTING client, so the client id is
    // mandatory - it is what tells the server to reuse the client row.
    if (isReviewMode && !selectedClientId) {
      setToast({ message: "Please select a client first", type: "error" });
      return null;
    }

    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/therapist_api/save_basic_details.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({
            // Send the EXACT mode, not a generic "review". Both review modes run
            // the same `review` pathway, so this is the only place the
            // distinction is recorded - save_basic_details.php persists it in
            // node 1, and /continue-assessment reads it back for the Appointment
            // Type column and to restore the right mode on resume.
            appointment_type: isSmartGoalMode
              ? "review smart goal"
              : isReviewMode
                ? "review"
                : "initial",
            client_name: client,
            age_group: ageRange,
            client_id: isReviewMode && selectedClientId ? parseInt(selectedClientId) : undefined,
            // For Review Smart Goal, pass the originating goal session id so
            // the revision context is preserved across Save for Later.
            goal_session_id: isSmartGoalMode && goalSessionId ? parseInt(goalSessionId) : undefined,
          })
        }
      );
      const json = await response.json();
      if (json.status) {
        const sid = json.sessionId || json.session_id || json.data?.sessionId || json.data?.session_id || null;
        if (sid) setCurrentSessionId(sid);
        return json;
      }
      if (isTokenError(json)) { dispatchSessionExpired(); return null; }
      // Server-side one-time-per-client guard (mirrors check_initial_eligibility).
      if (json.code === "initial_already_completed") {
        setInitialBlocked({ ...(json.existing || {}), message: json.message });
        return null;
      }
      setToast({ message: json.message || "Failed to save basic details", type: "error" });
      return null;
    } catch (error) {
      setToast({ message: error.message || "Network error", type: "error" });
      return null;
    }
  };

  const handleSaveDraft = async () => {
    if (!currentSessionId) return;
    try {
      const url = isReviewMode
        ? `${API_BASE}/therapist_api/save_draft_session.php`
        : `${API_BASE}/therapist_api/save_draft.php`;
      const currentQ = step >= 2 && !isGoalDetailStep && !showReviewGoalScreen ? visibleSteps[step - 2] : null;
      const lastNode = currentQ ? currentQ.node_number : (targetNodeRef.current || null);
      const body = isReviewMode
        ? {
            sessionId: parseInt(currentSessionId),
            // Exact mode, so "Save for Later" keeps node 1 accurate and
            // /continue-assessment labels the row correctly.
            appointment_type: isSmartGoalMode ? "review smart goal" : "review",
            client_name: client,
            age_group: ageRange,
            answers, hine_data: hineData, hai_data: haiData, last_node: lastNode
          }
        : { sessionId: parseInt(currentSessionId), last_node: lastNode };
      const response = await fetchWithTimeout(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const json = await response.json();
      if (json.status) console.log("? Draft Saved:", json.message);
      else {
        if (isTokenError(json)) { dispatchSessionExpired(); return; }
        console.error("? Failed to save draft:", json.message);
      }
    } catch (error) { console.error("Network error saving draft:", error.message); }
  };

  const handleKeywordClick = (targetStep) => { setIsGoalDetailStep(false); setShowReviewGoalScreen(false); setStep(targetStep); };

  // -- Target for /summarypage ------------------------------------------------
  // The summary page needs to know whether this is a Review Smart Goal run and,
  // if so, which session holds the goal being revised. It used to be reached by
  // a bare navigate("/summarypage"), which carried neither - so the smart-goal
  // context was dropped and "Save and View Intervention" opened a normal
  // Intervention page that creates a new goal rather than revising the old one.
  const summaryHref = () => {
    const params = new URLSearchParams();
    if (currentSessionId) params.set("sessionId", String(currentSessionId));
    if (isSmartGoalMode) {
      params.set("smartGoal", "1");
      // Fall back to the assessment session itself: get_smart_goal_context.php
      // treats goal_session_id = 0 as "use the client's latest goal session", so
      // an empty value here still resolves server-side rather than being lost.
      params.set("goalSessionId", goalSessionId ? String(goalSessionId) : String(currentSessionId || ""));
    }
    return `/summarypage?${params.toString()}`;
  };

  const handleNext = () => {
    if (showReviewGoalScreen) {
      navigate(summaryHref());
      return;
    }
    if (step === 1) {
      if (!isFormValid()) return;

      // Initial is one-time per client - verify with the server before
      // creating the client + session. Review and Review Smart Goal are
      // repeatable and skip this check entirely.
      if (session === APPT_INITIAL) {
        setInitialBlocked(null);
        checkInitialEligibility(client, ageRange)
          .then((json) => {
            if (json.status && json.eligible) {
              return handleSaveBasicDetails().then((saved) => { if (saved) setStep(2); });
            }
            if (json.code === "initial_already_completed") {
              setInitialBlocked({ ...(json.existing || {}), message: json.message });
              return;
            }
            setToast({ message: json.message || "Could not verify Initial Assessment eligibility", type: "error" });
          })
          .catch((error) => {
            setToast({ message: error.message || "Network error", type: "error" });
          });
        return;
      }

      // Review and Review Smart Goal ("Set New Goal for Existing Client"):
      // save_basic_details.php creates/resumes the review session and writes
      // Q1-Q3 (Review / Client / Age). setStep(2) is the 4th question onwards -
      // the previous assessment's Background Information & Assessments through
      // Comorbidities are pre-filled by the pre-fill effect, and the therapist
      // carries on from there.
      handleSaveBasicDetails().then((saved) => { if (saved) setStep(2); });
    } else {
      // If the selected option/sub-option chain of the current step carries
      // the "End" instruction, skip ahead straight to the summary page.
      if (step >= 2 && visibleSteps[step - 2]) {
        const currentQ = visibleSteps[step - 2];
        if (currentQ && selectedChainHasEnd(currentQ, answers)) {
          setStep(1 + visibleSteps.length + 1);
          return;
        }
      }
      if (isGoalDetailStep) { setIsGoalDetailStep(false); }
      else if (step >= 2 && visibleSteps[step - 2]) {
        const currentQ = visibleSteps[step - 2];
        const isGoalDomainQ = isGoalDomainQuestion(currentQ);
        const ans = answers[currentQ.node_number];
        if (isGoalDomainQ && Array.isArray(ans) && ans.length > 0) {
          const hasSubOptions = currentQ.options.some(opt => ans.includes(opt.option_text) && opt.sub_options && opt.sub_options.length > 0);
          if (hasSubOptions) { setIsGoalDetailStep(true); return; }
        }
      }
      const totalSteps = 1 + visibleSteps.length;
      if (step < totalSteps) { setStep(prev => prev + 1); }
      else if (step === totalSteps) { setStep(totalSteps + 1); }
      else if (step === totalSteps + 1) { navigate(summaryHref()); }
    }
  };

  const handleRestart = () => {
    localStorage.removeItem("assessmentState");
    localStorage.removeItem("reviewCarryOver");
    setSession(""); setClient(""); setAgeRange(""); setAnswers({});
    setHineData([{ id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }]);
    setHaiData([{ id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }]);
    setStep(1); setActivePathway(null); setIsGoalDetailStep(false);
    setOpenSections({ demographics: false, background: false, comorbidities: false });
    setCurrentSessionId(null); setInterventionsData([]); setActiveGoalTab(null);
    setShowReviewGoalScreen(false); setSelectedReviewSessionId(null); setClientSearchTerm("");
    setGoalSessionId(null); setSmartGoalContext(null);
    setInitialBlocked(null);
    targetNodeRef.current = null;
  };

  // -- Capture the three step-1 answers (Review / Client / Age) so goalreview's
  // "Continue Pathway" can pass them to the review page. These are the exact
  // values the therapist selected, stored so the review carries over even when
  // the client has no review session yet.
  //
  // Uses isReviewOption() rather than `=== APPT_REVIEW` so a Review Smart Goal
  // draft is captured too, and the real mode string is stored (not a
  // hardcoded "Review") so the consumer can restore the correct mode. -------
  useEffect(() => {
    if (isReviewOption(session) && client && ageRange) {
      localStorage.setItem("reviewCarryOver", JSON.stringify({ session, client, ageRange }));
    }
  }, [session, client, ageRange]);

  // -- Fetch favourite status when sessionId is available -------------------
  useEffect(() => {
    if (!currentSessionId || !token) return;
    const fetchFavourite = async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/pathway_favorites.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({ sessionId: parseInt(currentSessionId), action: "status" })
          }
        );
        const json = await response.json();
        if (json.status !== undefined) setIsFavourite(json.is_favourite === true);
      } catch (error) { console.error("Failed to fetch favourite status:", error.message); }
    };
    fetchFavourite();
  }, [currentSessionId, token]);

  const handleToggleFavourite = async () => {
    if (!currentSessionId) return;
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/therapist_api/pathway_favorites.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ sessionId: parseInt(currentSessionId), action: "toggle" })
        }
      );
      const json = await response.json();
      if (json.status) {
        setIsFavourite(json.is_favourite === true);
        console.log(json.message);
      }
    } catch (error) {
      console.error("Failed to toggle favourite:", error.message);
    }
  };

  const handleBack = () => {
    if (showReviewGoalScreen) { setShowReviewGoalScreen(false); return; }
    if (isGoalDetailStep) { setIsGoalDetailStep(false); return; }
    if (step > 1) {
      // Review questions start at step 2. Back must not drop into the step-1
      // Review setup screen, whose "Next" would create a NEW review session
      // and send the user back to Goal Progress Update / goalreview.
      if (isReviewMode && step === 2) return;
      setStep(prev => prev - 1);
    }
  };

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const isFormValid = () => {
    if (step === 1) return session && client.trim() !== "" && ageRange !== "";
    if (isGoalDetailStep) {
      const currentQ = visibleSteps[step - 2];
      if (!currentQ) return false;
      const mainAns = answers[currentQ.node_number];
      if (!mainAns || !Array.isArray(mainAns) || mainAns.length === 0) return false;
      for (const selectedOptText of mainAns) {
        const selectedOpt = currentQ.options?.find(o => o.option_text === selectedOptText);
        if (selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0) {
          const subKey = `${currentQ.node_number}_${selectedOptText}_sub`;
          if (!answers[subKey]) return false;
        }
      }
      return true;
    }
    const totalSteps = 1 + visibleSteps.length;
    if (step === totalSteps + 1) return true;
    const currentDynamicIndex = step - 2;
    const currentQ = visibleSteps[currentDynamicIndex];
    if (!currentQ) return false;
    const isGoalDomainQ = isGoalDomainQuestion(currentQ);
    if (isGoalDomainQ) { const ans = answers[currentQ.node_number]; return Array.isArray(ans) && ans.length > 0; }
    if (!currentQ.options || currentQ.options.length === 0) return true;
    const answer = answers[currentQ.node_number];
    if (!answer) return false;
    const selectedOpt = currentQ.options?.find(o => o.option_text === answer);
    if (selectedOpt?.sub_options?.length > 0) {
      const subAnswerKey = `${currentQ.node_number}_${answer}_sub`;
      const subAnswer = answers[subAnswerKey];
      if (Array.isArray(subAnswer)) return subAnswer.length > 0;
      return subAnswer !== undefined;
    }
    const instructions = parseInstructions(currentQ.instruction);
    if (instructions.isMulti) { const multiAns = answers[currentQ.node_number]; return Array.isArray(multiAns) && multiAns.length > 0; }
    return true;
  };

  // ---------------------------------------------------------------------------
  // DOWNLOAD & SHARE HELPERS
  // ---------------------------------------------------------------------------

  const openAllAccordions = () => {
    if (!interventionsData || !interventionsData.data) return () => { };
    const prevState = { ...accordionOpen };
    const allOpen = {};
    Object.keys(interventionsData.data).forEach(key => {
      (interventionsData.data[key] || []).forEach(intervention => {
        allOpen[`int-${intervention.id}`] = true;
      });
    });
    setAccordionOpen(allOpen);
    return () => setAccordionOpen(prevState);
  };

  const loadPdfLibraries = () => {
    return new Promise((resolve) => {
      const loadScript = (src) =>
        new Promise((res) => {
          if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
          const s = document.createElement("script");
          s.src = src;
          s.onload = res;
          document.head.appendChild(s);
        });

      Promise.all([
        loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"),
        loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"),
      ]).then(resolve);
    });
  };

  const captureSummaryAsPdfBlob = async () => {
    if (!summaryRef.current) throw new Error("Summary element not found");

    await loadPdfLibraries();

    await new Promise(r => setTimeout(r, 600));

    const element = summaryRef.current;
    const canvas = await window.html2canvas(element, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: -window.scrollY,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
    });

    const { jsPDF } = window.jspdf;
    const pdfWidth = 210;
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    const pdf = new jsPDF({ orientation: pdfHeight > pdfWidth ? "portrait" : "landscape", unit: "mm", format: "a4" });

    const pages = paginateCanvasToPdf({
      canvas,
      element,
      pdfWidth,
      pageHeightMm: pdf.internal.pageSize.getHeight(),
    });

    pages.forEach((p, i) => {
      if (i > 0) pdf.addPage();
      pdf.addImage(p.sliceImgData, "JPEG", 0, 0, pdfWidth, p.sliceHeightMm);
    });

    return pdf.output("blob");
  };

  // -- PREVIOUS DATA: download the open group's Initial + Review comparison
  // tables as a PDF. Captures the off-screen printable node, paginates it
  // onto A4 sheets with symmetric 12mm margins, and saves it. --------------
  const handleDownloadGroupData = async () => {
    setIsDownloadingGroupData(true);
    try {
      await loadPdfLibraries();
      await new Promise(r => setTimeout(r, 300));
      const node = groupHistoryPrintRef.current;
      if (!node) throw new Error("Printable node not found");

      const canvas = await window.html2canvas(node, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const { jsPDF } = window.jspdf;
      const pdfWidth = 210;
      const marginMm = 12;
      const contentWidthMm = pdfWidth - (marginMm * 2);
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pdf = new jsPDF({
        orientation: pdfHeight > pdfWidth ? "portrait" : "landscape",
        unit: "mm",
        format: "a4",
      });

      const pages = paginateCanvasToPdf({
        canvas,
        element: node,
        pdfWidth,
        pageHeightMm: pdf.internal.pageSize.getHeight(),
      });

      pages.forEach((p, i) => {
        if (i > 0) pdf.addPage();
        const scaledHeight = (p.sliceHeightMm / pdfWidth) * contentWidthMm;
        const pageHeight = pdf.internal.pageSize.getHeight();
        const yOffset = marginMm + Math.max(0, (pageHeight - (marginMm * 2) - scaledHeight) / 2);
        pdf.addImage(p.sliceImgData, "JPEG", marginMm, yOffset, contentWidthMm, scaledHeight);
      });

      const safeName = (historyGroupName || "group").replace(/[^a-zA-Z0-9-]/g, "-");
      pdf.save(`previous-data-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error("Group data PDF generation failed:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsDownloadingGroupData(false);
    }
  };

  const handleShareMail = async () => {
    setShowShareModal(false);
    setIsGeneratingPdf(true);
    const restoreAccordions = openAllAccordions();

    try {
      const blob = await captureSummaryAsPdfBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assessment-summary-${client || "report"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const subject = encodeURIComponent(`Assessment Summary ? ${client || "Client"}`);
      const body = encodeURIComponent(
        `Please find the assessment summary PDF attached.\n\nClient: ${client || "-"}\nAge Group: ${ageRange || "-"}\nSession Type: ${session || "-"}\n\nThe PDF has been downloaded to your device. Please attach it to this email before sending.`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    } catch (err) {
      console.error("Mail share failed:", err);
      alert("Failed to generate PDF for email. Please try again.");
    } finally {
      restoreAccordions();
      setIsGeneratingPdf(false);
    }
  };

  const handleShareWhatsApp = async () => {
    setShowShareModal(false);
    setIsGeneratingPdf(true);
    const restoreAccordions = openAllAccordions();

    try {
      const blob = await captureSummaryAsPdfBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assessment-summary-${client || "report"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const message = encodeURIComponent(
        `Assessment Summary for ${client || "Client"}\nAge Group: ${ageRange || "-"} | Session: ${session || "-"}\n\nThe PDF summary has been downloaded to your device. Please share it via WhatsApp by attaching the file.`
      );
      window.open(`https://wa.me/?text=${message}`, "_blank");
    } catch (err) {
      console.error("WhatsApp share failed:", err);
      alert("Failed to generate PDF for WhatsApp. Please try again.");
    } finally {
      restoreAccordions();
      setIsGeneratingPdf(false);
    }
  };

  const handleSummaryConfirm = async () => {
    if (!currentSessionId) return;

    try {
      // Review / Review Smart Goal confirm through the review endpoint, which
      // (like the rest of the Review flow) scopes the update to sessions owned
      // by the signed-in therapist. The Initial flow keeps using the original
      // endpoint unchanged.
      const summaryConfirmUrl = isReviewMode
        ? `${API_BASE}/therapist_api/review_summary_confirm.php`
        : `${API_BASE}/therapist_api/summary_confirm.php`;
      const response = await fetchWithTimeout(
        summaryConfirmUrl,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_id: parseInt(currentSessionId),
          }),
        }
      );

      const json = await response.json();

      if (json.status) {
        setToast({ message: "Summary Saved Successfully.", type: "success" });
        setTimeout(() => {
          if (isSmartGoalMode) {
            // Continue the Review Smart Goal pathway: Intervention page ->
            // "Revise Selected Intervention" -> Updategoal -> goalsetdownload.
            // fromReview=1 flips the button label/flow; goalSessionId keeps the
            // revision pointed at the goal that is actually being reviewed.
            const params = new URLSearchParams({ sessionId: String(currentSessionId), fromReview: "1" });
            if (goalSessionId) params.set("goalSessionId", String(goalSessionId));
            if (selectedClientId) params.set("clientId", String(selectedClientId));
            params.set("smartGoal", "1");
            navigate(`/Interventions?${params.toString()}`);
            return;
          }
          if (isReviewMode) {
            // Plain Review ("Set New Goal for Existing Client"): same Intervention
            // hand-off the Review flow has always used.
            navigate(`/Interventions?sessionId=${currentSessionId}&fromReview=1`);
            return;
          }
          navigate(`/Interventions?sessionId=${currentSessionId}`);
        }, 1500);
      } else {
        if (isTokenError(json)) { dispatchSessionExpired(); return; }
        alert(json.message || "Failed to confirm summary");
      }
    } catch (error) {
      console.error(error);
      alert("Network error");
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER HELPERS
  // ---------------------------------------------------------------------------
  const DescriptionContent = ({ html, color = "#323C43" }) => (
    <div
      className="description-content"
      style={{
        fontSize: "14px",
        color: "#323C43",
        textAlign: "left",
        lineHeight: "1.8",
        margin: 0,
      }}
      dangerouslySetInnerHTML={{
        __html: html || "",
      }}
    />
  );
  const renderAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;
    const colorOrder = { purple: 1, grey: 2, yellow: 3, orange: 4, red: 5, cyan: 6, blue: 7, green: 8 };
    const sorted = [...attachments].sort((a, b) => (colorOrder[a.color] || 99) - (colorOrder[b.color] || 99));
    return (
      <div style={{ marginTop: "15px" }}>
        {sorted.map((att, idx) => (
          <div key={idx} style={{ marginBottom: "10px" }}>
            {att.color === "green" && (
              <div>
                {att.descriptions && att.descriptions.length > 0 ? (
                  att.descriptions.map((desc, dIdx) => {
                    const url = desc.link && desc.link.trim() !== "" ? (desc.link.startsWith("http") ? desc.link : "https://" + desc.link) : null;
                    const extractUrlFromHtml = (html) => {
                      if (!html) return null;
                      const match = html.match(/href=["']([^"']+)["']/i);
                      return match ? match[1] : null;
                    };
                    return (
                      <div className="" key={dIdx}>
                        <button className="fact-btn10" onClick={() => {
                          if (url) {
                            window.open(url, "_blank");
                          } else if (desc.description) {
                            const href = extractUrlFromHtml(desc.description);
                            if (href) {
                              window.open(href.startsWith("http") ? href : "https://" + href, "_blank");
                            }
                          }
                        }}>
                          <FontAwesomeIcon icon={faDownload} className="header-icon10" aria-hidden="true" /> {desc.title || att.title || "Resource Link"}
                        </button>
                      </div>
                    );
                  })
                ) : att.file_path ? (
                  <div>
                    <button className="fact-btn10" onClick={() => {
                      const fileUrl = att.file_path.startsWith("http") ? att.file_path : `${API_BASE}/` + att.file_path;
                      window.open(fileUrl, "_blank");
                    }}>
                      <FontAwesomeIcon icon={faDownload} className="header-icon10" aria-hidden="true" /> {att.title || "Resource Link"}
                    </button>
                  </div>
                ) : null}
              </div>
            )}
            {att.color === "blue" && att.file_path && (
              <div style={{ marginTop: "10px", background: "#E6F7FC", borderRadius: "8px", padding: "10px" }}>
                <a
                  href={att.file_path.startsWith("http") ? att.file_path : `${API_BASE}/` + att.file_path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="attachment-image-link"
                  aria-label={att.title || "Open attachment image in new tab"}
                >
                  <img
                    src={att.file_path.startsWith("http") ? att.file_path : `${API_BASE}/` + att.file_path}
                    alt={att.title || "Attachment"}
                    style={{ maxWidth: "100%", maxHeight: "250px", borderRadius: "8px", objectFit: "contain", border: "1px solid #e0e0e0", display: "block" }}
                  />
                </a>
              </div>
            )}
            {att.color === "purple" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box0" key={dIdx}>
                {desc.title ? (
                  <>
                    <p style={{ fontSize: "14px", color: "#323C43", textAlign: "start", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                      <FontAwesomeIcon icon={faBookOpenReader} aria-hidden="true" style={{ marginTop: "5px" }} />
                      <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#323C43",
                        textAlign: "left",
                        paddingLeft: "22px",
                        lineHeight: "1.8",
                        whiteSpace: "pre-line",
                        margin: 0,
                      }}
                    >
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>                </>)
                  :
                  (<p style={{ fontSize: "14px", color: "#323C43", textAlign: "start", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faBookOpenReader} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <div style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}>
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>
                  </p>)}
              </div>
            ))}
            {att.color === "grey" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box" key={dIdx}>
                {desc.title ? (
                  <>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#323C43",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                      }}
                    >
                      <img
                        src={infoIcon}
                        alt="Info"
                        className="infoicon"
                        style={{
                          marginTop: "5px",
                        }}
                      />

                      <span style={{ fontWeight: "bold" }}>
                        {desc.title}
                      </span>
                    </p>

                    <div
                      style={{
                        fontSize: "14px",
                        color: "#323C43",
                        textAlign: "left",
                        paddingLeft: "22px",
                        lineHeight: "1.8",
                        whiteSpace: "pre-line",
                        margin: 0,
                      }}
                    >
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}
                  >
                    <img
                      src={infoIcon}
                      alt="Info"
                      className="infoicon"
                      style={{
                        marginTop: "5px",
                        minWidth: "13px",
                      }}
                    />

                    <DescriptionContent
                      html={desc.description}
                      color="#323C43"
                    />
                  </div>
                )}
              </div>
            ))}
            {att.color === "yellow" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box1" key={dIdx}>
                {desc.title ? (<><p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                  <img src={rightarrow} alt="Info" className="infoicon" style={{ width: "20px", height: "20px" }} /><span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                  <div
                    style={{
                      paddingLeft: "27px",
                      marginTop: "5px",
                    }}
                  >
                    <DescriptionContent
                      html={desc.description}
                      color="#323C43"
                    />
                  </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <img src={rightarrow} alt="Info" className="infoicon" style={{ width: "20px", height: "20px" }} />
                    <div style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}>
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>
                  </p>)}
              </div>
            ))}
            {att.color === "orange" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box2" key={dIdx}>
                {desc.title ? (<>
                  <p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faCopy} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                  <div
                    style={{
                      paddingLeft: "25px",
                      marginTop: "5px",
                    }}
                  >
                    <DescriptionContent
                      html={desc.description}
                      color="#323C43"
                    />
                  </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faCopy} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <div style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}>
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>
                  </p>)}
              </div>
            ))}
            {att.color === "red" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box3" key={dIdx}>
                {desc.title ? (<>
                  <p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                  <div
                    style={{
                      paddingLeft: "26px",
                      marginTop: "5px",
                    }}
                  >
                    <DescriptionContent
                      html={desc.description}
                      color="#323C43"
                    />
                  </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <div style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}>
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>
                  </p>)}
              </div>
            ))}
            {att.color === "cyan" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box4" key={dIdx}>
                {desc.title ? (<>
                  <p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faLink} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                  <div
                    style={{
                      paddingLeft: "24px",
                      marginTop: "5px",
                    }}
                  >
                    <DescriptionContent
                      html={desc.description}
                      color="#323C43"
                    />
                  </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faLink} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <div style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}>
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>

                  </p>)}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderSubOptionsChain = (qNode, pathTexts, subOptions, parentInstruction) => {
    if (!subOptions || subOptions.length === 0) return null;
    const subOptionLimit = parseSubOptionLimit(parentInstruction);
    const subInstructions = parseInstructions(parentInstruction);
    const isMultiSub = subOptionLimit > 1 || subInstructions.isMulti;
    const multiLimit = subOptionLimit > 1 ? subOptionLimit : null;
    const chainKey = [qNode, ...pathTexts, "sub"].join("_");
    const selectedValRaw = answers[chainKey];
    const selectedArr = Array.isArray(selectedValRaw) ? selectedValRaw : (selectedValRaw ? [selectedValRaw] : []);
    const selectedSubs = subOptions.filter(sub => selectedArr.includes(sub.option_text));
    const selectedSubAttachments = selectedSubs.reduce((acc, s) => acc.concat(s.attachments || []), []);

    return (
      <>
        {isMultiSub ? (
          multiLimit ? (
            <p className="question" style={{ marginBottom: "5px" }}>
              Please select up to <strong>{multiLimit}</strong> options:
              <span style={{ fontSize: "0.9em", color: "#666", marginLeft: "10px" }}>(Selected: {selectedArr.length}/{multiLimit})</span>
            </p>
          ) : (
            <p className="question" style={{ marginBottom: "5px" }}>
              Please select more than one option:
              <span style={{ fontSize: "0.9em", color: "#666", marginLeft: "10px" }}>(Selected: {selectedArr.length})</span>
            </p>
          )
        ) : (
          <p className="question">Please select from the list below:</p>
        )}
        <div className={`radio-group2 ${parentInstruction === "Vertical" ? "vertical" : ""}`}>
          {subOptions.map(sub => {
            const hasDeeper = sub.sub_options && sub.sub_options.length > 0;
            if (isMultiSub) {
              const isChecked = selectedArr.includes(sub.option_text);
              const isDisabled = multiLimit !== null && !isChecked && selectedArr.length >= multiLimit;
              return (
                <div key={sub.option_id} style={{ marginBottom: hasDeeper ? "10px" : "0" }}>
                  <label style={{ opacity: isDisabled ? 0.6 : 1, display: "block" }}>
                    <input type="checkbox" name={chainKey} value={sub.option_text} checked={isChecked} disabled={isDisabled}
                      onChange={() => {
                        let currSel = Array.isArray(answers[chainKey]) ? [...answers[chainKey]] : [];
                        let nextSel = isChecked ? currSel.filter(s => s !== sub.option_text) : [...currSel, sub.option_text];
                        handleChainSubOptionSelect(qNode, pathTexts, sub, true, multiLimit ?? subOptions.length);
                        let allSubIds = subOptions.filter(o => nextSel.includes(o.option_text)).map(o => o.option_id);
                        const targetNode = sub.node_number || qNode;
                        const parentIdForSave = sub.parent_option_id || sub.option_id;
                        saveResponseAPI(currentSessionId, targetNode, "option", parentIdForSave, null, allSubIds.length === 0 ? [] : allSubIds);
                      }} /> <RichText inline html={sub.option_text} />
                  </label>
                  {isChecked && hasDeeper && (
                    <div style={{ marginLeft: "30px", marginTop: "10px", borderLeft: "2px solid #ddd", paddingLeft: "10px" }}>
                      {renderSubOptionsChain(qNode, [...pathTexts, sub.option_text], sub.sub_options, sub.instruction || "")}
                    </div>
                  )}
                </div>
              );
            }
            const isSelectedRadio = selectedArr[0] === sub.option_text;
            return (
              <div key={sub.option_id} style={{ marginBottom: hasDeeper ? "10px" : "0" }}>
                <label style={{ display: "block" }}>
                  <input type="radio" name={chainKey} value={sub.option_text} checked={isSelectedRadio}
                    onChange={() => {
                      handleChainSubOptionSelect(qNode, pathTexts, sub, false, 1);
                      if (!isSelectedRadio) saveResponseAPI(currentSessionId, sub.node_number, "option", sub.option_id, sub.option_text);
                      else saveResponseAPI(currentSessionId, sub.node_number, "question", sub.option_id, "");
                    }} /> <RichText inline html={sub.option_text} />
                </label>
                {isSelectedRadio && (
                  <div style={{ marginLeft: "30px" }}>
                    {hasDeeper && (
                      <div style={{ borderLeft: "2px solid #ddd", paddingLeft: "10px", marginTop: "10px" }}>
                        {renderSubOptionsChain(qNode, [...pathTexts, sub.option_text], sub.sub_options, sub.instruction || "")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {renderAttachments(selectedSubAttachments)}
      </>
    );
  };

  // -- HINE UI ---------------------------------------------------------------
  const renderHINESection = (selectedVal) => {
    if (selectedVal !== "Yes") return null;
    return (
      <div className="hine-section">
        <div className="hine-row-single">
          <div className="hine-field time-field">
            <label htmlFor="hine-timepoint-0">Timepoint</label>
            <input
              type="text"
              id="hine-timepoint-0"
              placeholder="Enter age in months"
              value={hineData[0]?.timepoint || ""}
              className="hine-input"
              onChange={e => handleHineChange(0, "timepoint", e.target.value)}
            />
          </div>
          <div className="hine-field">
            <label htmlFor="hine-score-0">HINE Score</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHineDecrement(0, "hineScore")} aria-label="Decrease HINE Score">-</button>
              <input
                type="text"
                id="hine-score-0"
                value={hineData[0]?.hineScore ?? ""}
                onChange={e => handleHineChange(0, "hineScore", e.target.value)}
                onFocus={e => clearZeroOnFocusHine(e, 0, "hineScore")}
                onBlur={e => restoreZeroOnBlurHine(e, 0, "hineScore")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHineIncrement(0, "hineScore")} aria-label="Increase HINE Score">+</button>
            </div>
          </div>
          <div className="hine-field">
            <label htmlFor="hine-right-asym-0">Right Asymmetries</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHineDecrement(0, "rightAsym")} aria-label="Decrease Right Asymmetries">-</button>
              <input
                type="text"
                id="hine-right-asym-0"
                value={hineData[0]?.rightAsym ?? ""}
                onChange={e => handleHineChange(0, "rightAsym", e.target.value)}
                onFocus={e => clearZeroOnFocusHine(e, 0, "rightAsym")}
                onBlur={e => restoreZeroOnBlurHine(e, 0, "rightAsym")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHineIncrement(0, "rightAsym")} aria-label="Increase Right Asymmetries">+</button>
            </div>
          </div>
          <div className="hine-field">
            <label htmlFor="hine-left-asym-0">Left Asymmetries</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHineDecrement(0, "leftAsym")} aria-label="Decrease Left Asymmetries">-</button>
              <input
                type="text"
                id="hine-left-asym-0"
                value={hineData[0]?.leftAsym ?? ""}
                onChange={e => handleHineChange(0, "leftAsym", e.target.value)}
                onFocus={e => clearZeroOnFocusHine(e, 0, "leftAsym")}
                onBlur={e => restoreZeroOnBlurHine(e, 0, "leftAsym")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHineIncrement(0, "leftAsym")} aria-label="Increase Left Asymmetries">+</button>
            </div>
          </div>
        </div>
        <div className="hine-sub">
          <p className="hine-subtext">Please provide earlier HINE scores if any</p>
          <div className="hine-actions">
            <button type="button" className="add-btn" onClick={addHineRow} aria-label="Add HINE row">+</button>
            {hineData.length > 1 && <button type="button" className="remove-btn" onClick={() => removeHineRow(hineData.length - 1)} aria-label="Remove last HINE row">-</button>}
          </div>
        </div>
        {hineData.slice(1).map((item, index) => {
          const rowId = index + 1;
          return (
            <div key={item.id || index} className="hine-row-single">
              <div className="hine-field time-field">
                <label htmlFor={`hine-timepoint-${rowId}`}>Timepoint</label>
                <input
                  type="text"
                  id={`hine-timepoint-${rowId}`}
                  placeholder="Enter age in months"
                  value={item.timepoint || ""}
                  className="hine-input"
                  onChange={e => handleHineChange(rowId, "timepoint", e.target.value)}
                />
              </div>
              <div className="hine-field">
                <label htmlFor={`hine-score-${rowId}`}>HINE Score</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHineDecrement(rowId, "hineScore")} aria-label="Decrease HINE Score">-</button>
                  <input
                    type="text"
                    id={`hine-score-${rowId}`}
                    value={item.hineScore ?? ""}
                    onChange={e => handleHineChange(rowId, "hineScore", e.target.value)}
                    onFocus={e => clearZeroOnFocusHine(e, rowId, "hineScore")}
                    onBlur={e => restoreZeroOnBlurHine(e, rowId, "hineScore")}
                    inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHineIncrement(rowId, "hineScore")} aria-label="Increase HINE Score">+</button>
                </div>
              </div>
              <div className="hine-field">
                <label htmlFor={`hine-right-asym-${rowId}`}>Right Asymmetries</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHineDecrement(rowId, "rightAsym")} aria-label="Decrease Right Asymmetries">-</button>
                  <input
                    type="text"
                    id={`hine-right-asym-${rowId}`}
                    value={item.rightAsym ?? ""}
                    onChange={e => handleHineChange(rowId, "rightAsym", e.target.value)}
                    onFocus={e => clearZeroOnFocusHine(e, rowId, "rightAsym")}
                    onBlur={e => restoreZeroOnBlurHine(e, rowId, "rightAsym")}
                    inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHineIncrement(rowId, "rightAsym")} aria-label="Increase Right Asymmetries">+</button>
                </div>
              </div>
              <div className="hine-field">
                <label htmlFor={`hine-left-asym-${rowId}`}>Left Asymmetries</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHineDecrement(rowId, "leftAsym")} aria-label="Decrease Left Asymmetries">-</button>
                  <input
                    type="text"
                    id={`hine-left-asym-${rowId}`}
                    value={item.leftAsym ?? ""}
                    onChange={e => handleHineChange(rowId, "leftAsym", e.target.value)}
                    onFocus={e => clearZeroOnFocusHine(e, rowId, "leftAsym")}
                    onBlur={e => restoreZeroOnBlurHine(e, rowId, "leftAsym")}
                    inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHineIncrement(rowId, "leftAsym")} aria-label="Increase Left Asymmetries">+</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // -- HAI UI ----------------------------------------------------------------
  const renderHAISection = (selectedVal) => {
    if (selectedVal !== "Yes") return null;
    return (
      <div className="hai-section1">
        <div className="hine-row-single">
          <div className="hine-field time-field">
            <label htmlFor="hai-timepoint-0">Timepoint</label>
            <input
              type="text"
              id="hai-timepoint-0"
              placeholder="Enter age in months"
              value={haiData[0]?.timepoint || ""}
              className="hine-input"
              onChange={e => handleHaiChange(0, "timepoint", e.target.value)}
            />
          </div>
          <div className="hine-field">
            <label htmlFor="hai-right-eahs-0">Each hand score (Right)</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHaiDecrement(0, "rightEahs")} aria-label="Decrease Each hand score Right">-</button>
              <input
                type="text"
                id="hai-right-eahs-0"
                value={haiData[0]?.rightEahs ?? ""}
                onChange={e => handleHaiChange(0, "rightEahs", e.target.value)}
                onFocus={e => clearZeroOnFocusHai(e, 0, "rightEahs")}
                onBlur={e => restoreZeroOnBlurHai(e, 0, "rightEahs")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHaiIncrement(0, "rightEahs")} aria-label="Increase Each hand score Right">+</button>
            </div>
          </div>
          <div className="hine-field">
            <label htmlFor="hai-left-eahs-0">Each hand score (Left)</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHaiDecrement(0, "leftEahs")} aria-label="Decrease Each hand score Left">-</button>
              <input
                type="text"
                id="hai-left-eahs-0"
                value={haiData[0]?.leftEahs ?? ""}
                onChange={e => handleHaiChange(0, "leftEahs", e.target.value)}
                onFocus={e => clearZeroOnFocusHai(e, 0, "leftEahs")}
                onBlur={e => restoreZeroOnBlurHai(e, 0, "leftEahs")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHaiIncrement(0, "leftEahs")} aria-label="Increase Each hand score Left">+</button>
            </div>
          </div>
          <div className="hine-field">
            <label htmlFor="hai-sum-score-0">Each hand sum score</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHaiDecrement(0, "eachHandSumScore")} aria-label="Decrease Each hand sum score">-</button>
              <input
                type="text"
                id="hai-sum-score-0"
                value={haiData[0]?.eachHandSumScore ?? ""}
                onChange={e => handleHaiChange(0, "eachHandSumScore", e.target.value)}
                onFocus={e => clearZeroOnFocusHai(e, 0, "eachHandSumScore")}
                onBlur={e => restoreZeroOnBlurHai(e, 0, "eachHandSumScore")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHaiIncrement(0, "eachHandSumScore")} aria-label="Increase Each hand sum score">+</button>
            </div>
          </div>
          <div className="hine-field">
            <label htmlFor="hai-asymmetry-0">Asymmetry (%) difference between hands</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHaiDecrement(0, "asymmetryIndex")} aria-label="Decrease Asymmetry percentage">-</button>
              <input
                type="text"
                id="hai-asymmetry-0"
                value={haiData[0]?.asymmetryIndex ?? ""}
                onChange={e => handleHaiChange(0, "asymmetryIndex", e.target.value)}
                onFocus={e => clearZeroOnFocusHai(e, 0, "asymmetryIndex")}
                onBlur={e => restoreZeroOnBlurHai(e, 0, "asymmetryIndex")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHaiIncrement(0, "asymmetryIndex")} aria-label="Increase Asymmetry percentage">+</button>
            </div>
          </div>
        </div>
        <div className="hine-sub">
          <p className="hine-subtext">Please provide earlier HAI scores if any</p>
          <div className="hine-actions">
            <button type="button" className="add-btn" onClick={addHaiRow} aria-label="Add HAI row">+</button>
            {haiData.length > 1 && <button type="button" className="remove-btn" onClick={() => removeHaiRow(haiData.length - 1)} aria-label="Remove last HAI row">-</button>}
          </div>
        </div>
        {haiData.slice(1).map((item, index) => {
          const rowId = index + 1;
          return (
            <div key={item.id || index} className="hine-row-single">
              <div className="hine-field time-field">
                <label htmlFor={`hai-timepoint-${rowId}`}>Timepoint</label>
                <input
                  type="text"
                  id={`hai-timepoint-${rowId}`}
                  placeholder="Enter age in months"
                  value={item.timepoint || ""}
                  className="hine-input"
                  onChange={e => handleHaiChange(rowId, "timepoint", e.target.value)}
                />
              </div>
              <div className="hine-field">
                <label htmlFor={`hai-right-eahs-${rowId}`}>Each hand score (Right)</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHaiDecrement(rowId, "rightEahs")} aria-label="Decrease Each hand score Right">-</button>
                  <input
                    type="text"
                    id={`hai-right-eahs-${rowId}`}
                                        value={item.rightEahs ?? ""}
                                        onChange={e => handleHaiChange(rowId, "rightEahs", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "rightEahs")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "rightEahs")}
                                        inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHaiIncrement(rowId, "rightEahs")} aria-label="Increase Each hand score Right">+</button>
                </div>
              </div>
              <div className="hine-field">
                <label htmlFor={`hai-left-eahs-${rowId}`}>Each hand score (Left)</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHaiDecrement(rowId, "leftEahs")} aria-label="Decrease Each hand score Left">-</button>
                  <input
                    type="text"
                    id={`hai-left-eahs-${rowId}`}
                                        value={item.leftEahs ?? ""}
                                        onChange={e => handleHaiChange(rowId, "leftEahs", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "leftEahs")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "leftEahs")}
                                        inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHaiIncrement(rowId, "leftEahs")} aria-label="Increase Each hand score Left">+</button>
                </div>
              </div>
              <div className="hine-field">
                <label htmlFor={`hai-sum-score-${rowId}`}>Each hand sum score</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHaiDecrement(rowId, "eachHandSumScore")} aria-label="Decrease Each hand sum score">-</button>
                  <input
                    type="text"
                    id={`hai-sum-score-${rowId}`}
                                        value={item.eachHandSumScore ?? ""}
                                        onChange={e => handleHaiChange(rowId, "eachHandSumScore", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "eachHandSumScore")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "eachHandSumScore")}
                                        inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHaiIncrement(rowId, "eachHandSumScore")} aria-label="Increase Each hand sum score">+</button>
                </div>
              </div>
              <div className="hine-field">
                <label htmlFor={`hai-asymmetry-${rowId}`}>Asymmetry (%) difference between hands</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHaiDecrement(rowId, "asymmetryIndex")} aria-label="Decrease Asymmetry percentage">-</button>
                  <input
                    type="text"
                    id={`hai-asymmetry-${rowId}`}
                                        value={item.asymmetryIndex ?? ""}
                                        onChange={e => handleHaiChange(rowId, "asymmetryIndex", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "asymmetryIndex")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "asymmetryIndex")}
                                        inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHaiIncrement(rowId, "asymmetryIndex")} aria-label="Increase Asymmetry percentage">+</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // PREVIOUS DATA - SHARED TABLE BUILDER
  // ---------------------------------------------------------------------------
  // Turns the raw sessions returned by get_historical_pathway_data.php into one
  // table per group. Kept separate from the markup so the on-screen panel and
  // the off-screen print copy below can never drift apart.
  const buildHistoryTables = () => {
    const gh = groupHistory;
    if (!gh || !Array.isArray(gh.data) || gh.data.length === 0) return null;

    const sessions = [...gh.data].sort(
      (a, b) => new Date(a.assessment_date || 0) - new Date(b.assessment_date || 0)
    );
    const shades = ["#A0C7E8", "#BED8EE", "#D3E4F5", "#E7F0FA"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const ordinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    const formatDate = (ds) => {
      if (!ds) return "-";
      const d = new Date(ds);
      if (isNaN(d.getTime())) return String(ds);
      return `${ordinal(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}`;
    };

    // Label each column Initial / Review 1 / Review 2 ... in chronological
    // order so the columns read as a timeline rather than a list.
    let reviewCount = 0;
    const colMeta = sessions.map((s, i) => {
      const isReview = !isInitialAppointmentType(s.appointment_type || s.type);
      if (isReview) reviewCount += 1;
      return {
        session: s,
        shade: shades[i % shades.length],
        label: isReview ? `Review ${reviewCount}` : "Initial",
        date: formatDate(s.assessment_date),
      };
    });

    const sessionQ = (s, type) =>
      (Array.isArray(s.pathway) ? s.pathway : []).find(
        (x) => String(x.type || "").trim().toUpperCase() === type
      );
    const anyQ = (type) =>
      sessions.some((s) =>
        (Array.isArray(s.pathway) ? s.pathway : []).some(
          (x) => String(x.type || "").trim().toUpperCase() === type
        )
      );
    const joinBracketed = (vals) => {
      const kept = vals.filter((v) => v !== "" && v !== null && v !== undefined);
      return kept.length ? kept.map((v, i) => (i === 0 ? String(v) : `(${v})`)).join(", ") : "-";
    };

    const tables = [];

    if (historyGroupName === "Demographics") {
      tables.push({
        title: "Demographics",
        rows: [
          { label: "Appointment", cell: (s) => formatAppointmentLabel(s.appointment_type) },
          { label: "Client", cell: (s) => s.client_name || gh.client_name || "-" },
          { label: "Client ID", cell: () => gh.client_id || historyClientId || "-" },
          { label: "Age Group", cell: (s) => s.age_group || gh.age_group || "-" },
        ],
      });
    } else {
      // One row per distinct question across every session, so a question
      // answered later still lines up against its earlier answers.
      const seen = new Set();
      const qRows = [];
      sessions.forEach((s) => {
        (Array.isArray(s.pathway) ? s.pathway : []).forEach((q) => {
          const type = String(q.type || "").trim().toUpperCase();
          if (type === "HINE" || type === "HAI") return;
          const keyword = q.keyword || q.question || "";
          if (!keyword) return;
          const kLower = keyword.toLowerCase();
          if (kLower.includes("hine") || kLower.includes("hai")) return;
          if (seen.has(keyword)) return;
          seen.add(keyword);
          qRows.push({
            label: keyword,
            cell: (ss) => {
              const match = (Array.isArray(ss.pathway) ? ss.pathway : []).find(
                (x) => (x.keyword || x.question || "") === keyword
              );
              return match ? formatHistoryAnswer(match.answer) : "-";
            },
          });
        });
      });
      if (qRows.length > 0) tables.push({ title: historyGroupName, rows: qRows });
    }

    // HINE / HAI keep their own tables: their answers are multi-entry score
    // sheets, not single values, and they do not belong to one group.
    if (anyQ("HINE")) {
      const entries = (s) => (sessionQ(s, "HINE")?.answer?.entries || []);
      tables.push({
        title: "HINE",
        rows: [
          { label: "Status", cell: (s) => (sessionQ(s, "HINE")?.answer?.value ? String(sessionQ(s, "HINE").answer.value) : "-") },
          { label: "Time Point", cell: (s) => joinBracketed(entries(s).map((e) => e.timepoint)) },
          { label: "HINE Score", cell: (s) => joinBracketed(entries(s).map((e) => e.hine_score)) },
          { label: "Right Asym.", cell: (s) => joinBracketed(entries(s).map((e) => e.right_asymmetry)) },
          { label: "Left Asym.", cell: (s) => joinBracketed(entries(s).map((e) => e.left_asymmetry)) },
        ],
      });
    }

    if (anyQ("HAI")) {
      const entries = (s) => (sessionQ(s, "HAI")?.answer?.entries || []);
      tables.push({
        title: "HAI",
        rows: [
          { label: "Status", cell: (s) => (sessionQ(s, "HAI")?.answer?.value ? String(sessionQ(s, "HAI").answer.value) : "-") },
          { label: "Time Point", cell: (s) => joinBracketed(entries(s).map((e) => e.timepoint)) },
          { label: "Right Score", cell: (s) => joinBracketed(entries(s).map((e) => e.right_score)) },
          { label: "Left Score", cell: (s) => joinBracketed(entries(s).map((e) => e.left_score)) },
          { label: "Sum (EAHS)", cell: (s) => joinBracketed(entries(s).map((e) => e.sum)) },
          { label: "Asymmetry", cell: (s) => joinBracketed(entries(s).map((e) => e.asymmetry)) },
        ],
      });
    }

    return { colMeta, tables };
  };

  const renderHistoryTables = (forPrint) => {
    const built = buildHistoryTables();
    if (!built) return null;
    if (built.tables.length === 0) {
      return (
        <p style={{ color: "#888", fontSize: "14px", textAlign: "center", padding: "40px 0" }}>
          No previous data found for this group.
        </p>
      );
    }

    const { colMeta, tables } = built;
    const cellBorder = "1px solid #d9e2ec";

    return tables.map((t, ti) => (
      <div
        key={ti}
        style={{
          marginBottom: "24px",
          overflowX: forPrint ? "visible" : "auto",
          WebkitOverflowScrolling: "touch",
          borderRadius: "8px",
          border: cellBorder,
        }}
      >
        <table
          style={{
            borderCollapse: "collapse",
            fontSize: "13px",
            width: "100%",
            tableLayout: "fixed",
            background: "#fff",
          }}
        >
          <colgroup>
            <col style={{ width: "26%" }} />
            {colMeta.map((c, ci) => (
              <col key={ci} style={{ width: `${74 / Math.max(colMeta.length, 1)}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                colSpan={colMeta.length + 1}
                style={{ background: "#0063A5", color: "#fff", textAlign: "center", padding: "10px 12px", fontWeight: "bold", fontSize: "14px", border: cellBorder }}
              >
                {t.title}
              </th>
            </tr>
            <tr>
              <th style={{ background: "#0063A5", color: "#fff", textAlign: "left", padding: "8px 10px", fontWeight: "bold", border: cellBorder }}>
                Assessment date
              </th>
              {colMeta.map((c, ci) => (
                <th key={ci} style={{ background: c.shade, color: "#1A1A1A", textAlign: "center", padding: "8px 10px", border: cellBorder }}>
                  <div>{c.date}</div>
                  <div style={{ fontSize: "11px", opacity: 0.85, fontWeight: "normal" }}>[{c.label}]</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {t.rows.map((row, r) => (
              <tr key={r}>
                <td style={{ background: "#0063A5", color: "#fff", fontWeight: "bold", textAlign: "left", padding: "8px 10px", verticalAlign: "top", border: cellBorder }}>
                  {row.label}
                </td>
                {colMeta.map((c, ci) => (
                  <td key={ci} style={{ background: c.shade, color: "#1A1A1A", verticalAlign: "top", wordBreak: "break-word", padding: "8px 10px", border: cellBorder }}>
                    {row.cell(c.session) || "-"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ));
  };

  const renderPreviousDataTablesForPrint = () => renderHistoryTables(true);

  // ---------------------------------------------------------------------------
  // RENDER PREVIOUS DATA PANEL
  // ---------------------------------------------------------------------------
  // Opened from the folder icon on the question-card during a Review session.
  // Lists every past completed Initial + Review session for this client, one
  // column per session, so a Review answer can be compared against the full
  // timeline rather than only the last session.
  const renderPreviousDataPanel = () => {
    if (!showHistoryFiles && !historyGroupName) return null;

    return (
      <div ref={historyViewRef} className="group-history-inline" style={{ background: "#fff", borderTop: "1px solid #ececec", padding: "4px 18px 18px", minHeight: "300px" }}>
        {/* -- Top bar: back / title / download PDF / close --
            Rendered for the WHOLE pane, not only for an open group. The pane
            is full width, so while only the group file bar was showing (folder
            opened, nothing picked yet) this bar did not exist and there was no
            Previous Page and no X - the only way out was the folder icon. */}
        {(showHistoryFiles || historyGroupName) && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", background: "#fff", border: "1px solid #ececec", borderRadius: "8px", padding: "5px 18px", margin: "5px 0", flexWrap: "wrap" }}>
            {/* CONDITION: "Previous Page" only exists once a group is open.
                With just the group bar there is no previous page to go back to
                inside Previous Data - "X" is the way out of the pane. */}
            {historyGroupName && (
              <button className="backbutton" onClick={handleHistoryBack} style={{ marginTop: 0, marginBottom: 0 }}>
                <FontAwesomeIcon icon={faArrowLeft} /> Previous Page
              </button>
            )}
            <div style={{ textAlign: "center", flex: "1", minWidth: "0", margin: "0 8px" }}>
              <h3 style={{ margin: 0, fontSize: "clamp(16px, 4vw, 21px)", fontWeight: "bold", color: "#1F7A26" }}>
                {historyGroupName ? "Client History" : "Previous Data"}
              </h3>
              {groupHistory?.client_name && (
                <p style={{ margin: "4px 0 0", fontSize: "clamp(13px, 3vw, 15px)", color: "#333", wordBreak: "break-word" }}>
                  <span style={{ fontWeight: "bold" }}>{groupHistory.client_name}</span>
                  {groupHistory?.client_code ? ` - ${groupHistory.client_code}` : ""}
                </p>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
              {/* CONDITION: the PDF is built from the open group's tables, so it
                  is only offered once a group with data is actually on screen. */}
              {historyGroupName && (
                <button
                  className="group-history-download-btn"
                  onClick={handleDownloadGroupData}
                  disabled={isDownloadingGroupData || !groupHistory || !groupHistory.data?.length}
                  title="Download as PDF"
                  aria-label="Download as PDF"
                  style={{ background: "#fff", border: "1.5px solid #1F7A26", color: "#1F7A26", fontFamily: "inherit", fontSize: "14px", fontWeight: "bold", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px", flexShrink: 0 }}
                >
                  <FontAwesomeIcon icon={faDownload} aria-hidden="true" />
                  {isDownloadingGroupData ? "Generating..." : "Download  PDF"}
                </button>
              )}
              {/* CONDITION: "X" closes the whole Previous Data view, so it is
                  always available while the pane is open. */}
              <button
                onClick={handleCloseHistory}
                aria-label="Close history view"
                title="Close Previous Data"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#666", fontSize: "18px" }}
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {/* -- Group names (files) bar -- */}
        {showHistoryFiles && (
          <div className="previous-data-wrapper" style={{ marginTop: 0 }}>
            <div className="previous-steps">
              {orderedHistoryGroups.map((group, gIdx) => {
                const isHistoryOpen = historyGroupName === group.group_name;
                const stepColors = ["active-purple", "active-gray", "active-orange", "active-red", "active-blue"];
                return (
                  <div
                    key={group.node_number || gIdx}
                    className={`step-item ${stepColors[gIdx % stepColors.length] || "active-purple"}`}
                    style={{ cursor: "pointer", borderRadius: "8px", border: isHistoryOpen ? "1px solid #2b8c29" : "1px solid transparent", background: isHistoryOpen ? "#f0f0f0" : "transparent", transition: "all 0.2s ease" }}
                    onClick={() => handleGroupHistoryClick(group.group_name)}
                    title={`View previous data for ${group.group_name}`}
                  >
                    <FaFileAlt className="step-icon" aria-hidden="true" />
                    <span style={{ color: isHistoryOpen ? "#1F7A26" : "#111" }}>{group.group_name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* -- Inline group history tables -- */}
        {historyGroupName && (
          <div style={{ paddingTop: "14px" }}>
            {loadingGroupHistory && (
              <p style={{ color: "#888", fontSize: "14px", textAlign: "center", padding: "40px 0" }} role="status" aria-live="polite">Loading previous answers...</p>
            )}

            {!loadingGroupHistory && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length === 0 && historyGroupName === "Demographics" && (
              <div style={{ padding: "12px 16px" }}>
                <p style={{ margin: "0 0 10px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                  <span style={{ fontWeight: 700 }}>Appointment</span>
                  <span> - {formatAppointmentLabel(groupHistory.appointment_type)}</span>
                </p>
                <p style={{ margin: "0 0 10px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                  <span style={{ fontWeight: 700 }}>Client</span>
                  <span> - {groupHistory.client_name || "-"}</span>
                </p>
                <p style={{ margin: "0 0 10px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                  <span style={{ fontWeight: 700 }}>Client ID</span>
                  <span> - {groupHistory?.client_id || historyClientId || "-"}</span>
                </p>
                <p style={{ margin: "0 0 0px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                  <span style={{ fontWeight: 700 }}>Age group</span>
                  <span> - {groupHistory.age_group || "-"}</span>
                </p>
              </div>
            )}

            {!loadingGroupHistory && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length === 0 && historyGroupName !== "Demographics" && (
              <p style={{ color: "#888", fontSize: "14px", textAlign: "center", padding: "40px 0" }}>No previous data found for this group.</p>
            )}

            {!loadingGroupHistory && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length > 0 && renderHistoryTables(false)}
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // PREVIOUS DATA: full-width state
  // ---------------------------------------------------------------------------
  // Opening the Previous Data folder swaps the WHOLE assessment area - the left
  // panel AND the right content - for the history view. The session-by-session
  // tables are far wider than a single question, and rendering them inside the
  // 75% right-hand column squeezed every table into a sideways scroll.
  const isHistoryPaneOpen = isReviewMode && (showHistoryFiles || historyGroupName);

  // Off-screen printable copy of the open group's history tables. html2canvas can
  // only capture what is laid out, so the node is rendered (but visually hidden)
  // rather than reusing the interactive table above.
  //
  // Rendered OUTSIDE both layout branches on purpose: the interactive panel is
  // replaced by the full-width pane, so leaving this copy inside `.right-content`
  // would unmount it and break "Download as PDF".
  const renderHistoryPrintCopy = () =>
    isReviewMode && historyGroupName && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length > 0 ? (
      <div
        ref={groupHistoryPrintRef}
        aria-hidden="true"
        style={{ position: "absolute", left: "-10000px", top: "0", width: "1100px", background: "#fff", padding: "24px" }}
      >
        <h2 style={{ margin: "0 0 4px", fontSize: "20px", color: "#1F7A26" }}>{historyGroupName}</h2>
        <p style={{ margin: "0 0 16px", fontSize: "14px", color: "#333" }}>
          <strong>{groupHistory?.client_name || client || "-"}</strong>
          {groupHistory?.client_code ? ` - ${groupHistory.client_code}` : ""}
        </p>
        {renderPreviousDataTablesForPrint()}
      </div>
    ) : null;

  // ---------------------------------------------------------------------------
  // RENDER CURRENT DYNAMIC QUESTION
  // ---------------------------------------------------------------------------

  const renderCurrentQuestion = () => {
    if (step < 2 || isGoalDetailStep) return null;
    if (step > 1 + visibleSteps.length) return null;

    const currentQ = visibleSteps[step - 2];
    if (!currentQ) return null;

    const prevQ = visibleSteps[step - 3];
    const isNewGroup = !prevQ || prevQ.groupNode !== currentQ.groupNode;
    const demographicsInfoText = "Understanding the infant's family context, supports, environment, and available resources is essential when selecting interventions, as these factors directly influence what is realistic, feasible, and sustainable. Effective intervention planning must integrate best-available evidence with family-centred practice and shared decision-making to ensure the chosen approach fits the infant's goals and everyday life.";
    let groupInfoText = null;
    if (isNewGroup) {
      groupInfoText = currentQ.groupName === "Demographics" ? demographicsInfoText : currentQ.groupInfo;
    }

    const continuousNumber = getContinuousNumber(currentQ);
    const instructions = parseInstructions(currentQ.instruction);
    const { isHINE, isHAI } = getQuestionType(currentQ);
    const isGoalDomain = isGoalDomainQuestion(currentQ);
    const selectedVal = answers[currentQ.node_number];

    let selectedOptionsList = [];
    if (selectedVal && currentQ.options) {
      if (Array.isArray(selectedVal)) {
        selectedOptionsList = currentQ.options.filter(o => selectedVal.includes(o.option_text));
      } else {
        const opt = currentQ.options.find(o => o.option_text === selectedVal);
        if (opt) selectedOptionsList = [opt];
      }
    }

    const hasOptions = currentQ.options && currentQ.options.length > 0;

    const goalDomainSelectedCount = isGoalDomain
      ? (Array.isArray(selectedVal) ? selectedVal.length : 0)
      : 0;

    return (
      <>
        {/* Group heading. The appointment type is part of the heading so a
            Review is never mistaken for the Initial that produced the same
            group of questions. */}
       
        {groupInfoText && (
          <div className="question-cardtop" style={{ borderLeft: "5px solid #288537", marginBottom: "0px" }}>
            <RichText inline html={groupInfoText} style={{ fontWeight: "bold", fontSize: "16px", color: "#288537", margin: 0 }} as="p" />
          </div>
        )}
        <div className="question-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
            <p className="question">{continuousNumber}. <RichText inline html={currentQ.question_text} /></p>
            {/* PREVIOUS DATA: folder icon, top-right of the question-card.
                Review sessions only - an Initial is the first session, so it
                has nothing prior to compare against. */}
            {isReviewMode && (
              <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                <span className="previous-data-label">Previous Data</span>
                <button
                  className="group-history-file-btn"
                  onClick={handleHistoryFileClick}
                  title="View all previous Initial and Review data for this client"
                  aria-label="View all previous Initial and Review data for this client"
                  aria-expanded={showHistoryFiles}
                >
                  <FaFolder aria-hidden="true" />
                </button>
              </div>
            )}
          </div>

          {instructions.isMulti && !isGoalDomain && (
            <p style={{ fontSize: "16px", marginBottom: "15px", fontWeight: 500, paddingLeft: "3px" }}>
              Please select more than one option
            </p>
          )}

          {hasOptions && (
            <div className={`radio-group ${instructions.isVertical ? "vertical" : ""}`}>
              {currentQ.options.map(opt => {
                if (isGoalDomain) {
                  const isSelected = Array.isArray(selectedVal) && selectedVal.includes(opt.option_text);
                  const isDisabled = !isSelected && goalDomainSelectedCount >= 2;
                  return (
                    <label key={opt.option_id} style={{ opacity: isDisabled ? 0.5 : 1 }}>
                      <input type="checkbox" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => {
                          const allowed = handleMainOptionSelect(currentQ.node_number, opt, true, true);
                          if (!allowed) return;
                          let curr = Array.isArray(answers[currentQ.node_number]) ? [...answers[currentQ.node_number]] : [];
                          let next = isSelected ? curr.filter(s => s !== opt.option_text) : [...curr, opt.option_text];
                          let allIds = currentQ.options.filter(o => next.includes(o.option_text)).map(o => o.option_id);
                          saveResponseAPI(currentSessionId, currentQ.node_number, "option", currentQ.question_id, null, allIds.length === 0 ? [] : allIds);
                        }} /> <RichText inline html={opt.option_text} />
                    </label>
                  );
                }

                if (instructions.isMulti) {
                  const multiVals = Array.isArray(selectedVal)
                    ? selectedVal
                    : (selectedVal !== undefined && selectedVal !== null && selectedVal !== ""
                      ? [selectedVal]
                      : []);
                  const isSelected = multiVals.includes(opt.option_text);
                  return (
                    <label key={opt.option_id}>
                      <input type="checkbox" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                        onChange={() => {
                          handleMainOptionSelect(currentQ.node_number, opt, true);
                          let curr = Array.isArray(answers[currentQ.node_number])
                            ? [...answers[currentQ.node_number]]
                            : (answers[currentQ.node_number] ? [answers[currentQ.node_number]] : []);
                          let next = isSelected ? curr.filter(s => s !== opt.option_text) : [...curr, opt.option_text];
                          let allIds = currentQ.options.filter(o => next.includes(o.option_text)).map(o => o.option_id);
                          saveResponseAPI(currentSessionId, currentQ.node_number, "option", currentQ.question_id, null, allIds.length === 0 ? [] : allIds);
                        }} /> <RichText inline html={opt.option_text} />
                    </label>
                  );
                }

                if (isHINE || isHAI) {
                  const type = isHINE ? "hine" : "hai";
                  const isSelected = selectedVal === opt.option_text;
                  return (
                    <label key={opt.option_id}>
                      <input type="radio" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                        onChange={() => {
                          handleMainOptionSelect(currentQ.node_number, opt, false);
                          if (!isSelected) {
                            saveResponseAPI(currentSessionId, currentQ.node_number, "option", opt.option_id, opt.option_text);
                            saveHineHaiAPI(
                              currentSessionId, currentQ.node_number, type, currentQ.question_id, opt.option_text,
                              opt.option_text === "Yes"
                                ? (isHINE
                                  ? hineData.map(r => ({ timepoint: r.timepoint, hine_score: r.hineScore, right_asymmetry: r.rightAsym, left_asymmetry: r.leftAsym }))
                                  : haiData.map(r => ({ timepoint: r.timepoint, right_score: r.rightEahs, left_score: r.leftEahs, sum: r.eachHandSumScore, asymmetry: r.asymmetryIndex })))
                                : []
                            );
                          } else {
                            saveResponseAPI(currentSessionId, currentQ.node_number, "question", opt.option_id, "");
                          }
                        }} /> <RichText inline html={opt.option_text} />
                    </label>
                  );
                }

                const isSelected = selectedVal === opt.option_text;
                return (
                  <label key={opt.option_id}>
                    <input type="radio" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                      onChange={() => {
                        handleMainOptionSelect(currentQ.node_number, opt, false);
                        if (!isSelected) {
                          saveResponseAPI(currentSessionId, currentQ.node_number, "option", opt.option_id, opt.option_text);
                        } else {
                          saveResponseAPI(currentSessionId, currentQ.node_number, "question", opt.option_id, "");
                        }
                      }} /> <RichText inline html={opt.option_text} />
                  </label>
                );
              })}
            </div>
          )}

          {!instructions.isMulti && !isGoalDomain && selectedOptionsList.length > 0 && (
            <div style={{ marginTop: "15px" }}>
              {selectedOptionsList.map(selectedOpt => {
                if (!selectedOpt.sub_options || selectedOpt.sub_options.length === 0) return null;
                return (
                  <div key={selectedOpt.option_id}>
                    {renderSubOptionsChain(currentQ.node_number, [selectedOpt.option_text], selectedOpt.sub_options, selectedOpt.instruction || "")}
                  </div>
                );
              })}
            </div>
          )}

          {isHINE && renderHINESection(selectedVal)}
          {isHAI && renderHAISection(selectedVal)}

          {selectedOptionsList.map(opt => renderAttachments(opt.attachments))}
          {renderAttachments(currentQ.attachments)}
        </div>
      </>
    );
  };

  // ---------------------------------------------------------------------------
  // LAYOUT SUB-RENDER HELPERS
  // ---------------------------------------------------------------------------
  // These are kept as functions inside the component (rather than top-level
  // components) so they can read every state value and handler with no prop
  // drilling. They simply break the giant JSX below into small, named pieces.

  // -- Small primitive shared by every summary row in the left panel --------
  const renderSummaryItem = ({ label, value, num, onClick, ariaLabel }) => (
    <li
      style={{
        color: "#2a2b2a",
        position: "relative",
        zIndex: 1,
        cursor: "pointer",
      }}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      role="button"
      tabIndex="0"
      aria-label={ariaLabel || `${label}: ${value || 'not set'}`}
    >
      <span
        className="step-number"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
          background: "#fff",
          borderRadius: "50%",
          fontSize: "14px",
          margin: "5px",
        }}
        aria-hidden="true"
      >
        {num}
      </span>
      <div className="content-row" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
        <span className="keyword-text">{label}</span>
        <span
          style={{
            color: "#999",
            backgroundColor: "white",
            border: "none",
            borderRadius: "0%",
            fontWeight: "bold",
            marginLeft: "4px",
            marginRight: "4px",
          }}
          aria-hidden="true"
        >
          -
        </span>
        <p style={{ fontWeight: "normal" }}><RichText html={value || ""} /></p>
      </div>
    </li>
  );

  // -- Left-panel "Demographics" block (used on review-goal + question steps) --
  const renderDemographicsPanel = ({ refCircle, connector }) => (
    <div className="panel-block expanded">
      <div
        className={`circle ${step > 1 ? "done" : ""}`}
        ref={refCircle}
        aria-hidden="true"
      >
        {step > 1 ? <FontAwesomeIcon icon={faCheck} /> : "I"}
      </div>
      <h3
        className="section-title"
        onClick={() => toggleSection("demographics")}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection("demographics"); } }}
        role="button"
        aria-expanded={openSections.demographics}
        tabIndex="0"
      >
        Demographics{" "}
        <span className="arrow" aria-hidden="true">
          {openSections.demographics ? <FontAwesomeIcon icon={faCaretDown} /> : <FontAwesomeIcon icon={faCaretUp} />}
        </span>
      </h3>
      {openSections.demographics && (
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: "16px",
              top: "19px",
              bottom: "10px",
              width: "3px",
              background: "#2b8c29",
              zIndex: 0,
            }}
            aria-hidden="true"
          />
          <ul className="sub-list1" aria-label="Demographics summary">
          {[
            { label: "Appointment", value: session, num: 1 },
            { label: "Client", value: client, num: 2 },
            { label: "Age", value: ageRange, num: 3 },
          ].map((item) => renderSummaryItem({ ...item, onClick: () => handleKeywordClick(1) }))}
          </ul>
        </div>
      )}
      {connector && step > 1 && <div className="connector11 done" aria-hidden="true" />}
    </div>
  );

  // -- Left-panel group (II, III, ...) blocks with their summary questions --
  const renderPathwayGroupPanels = () => (
    activePathway?.groups?.map((group, gIdx) => {
      if (group.group_name.toLowerCase() === "demographics") return null;
      const groupQuestions = visibleSteps.filter(q => q.groupNode === group.node_number);
      const firstQIdx = visibleSteps.findIndex(q => q.groupNode === group.node_number);
      const lastQIdx = visibleSteps.findLastIndex(q => q.groupNode === group.node_number);
      const isGroupActive = firstQIdx !== -1 && (step - 2 >= firstQIdx && step - 2 <= lastQIdx);
      const hasGroupAnswers = groupQuestions.some(q => answers[q.node_number]);
      const isGroupDone = lastQIdx !== -1 && (step - 2 > lastQIdx);
      if (!isGroupActive && !hasGroupAnswers) return null;
      const isLastGroup = gIdx === activePathway.groups.length - 1;
      const totalSteps = 1 + visibleSteps.length;
      const isSummaryPageActive = step === totalSteps + 1;
      const romanNumerals = ["II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
      return (
        <div key={group.node_number} className="panel-block expanded">
          <div
            className={`circle ${isGroupDone || isSummaryPageActive ? "done" : ""}`}
            ref={(isGroupDone || isSummaryPageActive) ? activeStepRef : null}
            aria-hidden="true"
          >
            {(isGroupDone || isSummaryPageActive) ? <FontAwesomeIcon icon={faCheck} /> : (romanNumerals[gIdx] || <FontAwesomeIcon icon={faDotCircle} />)}
          </div>
          <h3
            className="section-title"
            onClick={() => toggleSection(group.group_name)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(group.group_name); } }}
            role="button"
            aria-expanded={!!openSections[group.group_name]}
            tabIndex="0"
          >
            {group.group_name}{" "}
            <span className="arrow" aria-hidden="true">
              {openSections[group.group_name] ? <FontAwesomeIcon icon={faCaretDown} /> : <FontAwesomeIcon icon={faCaretUp} />}
            </span>
          </h3>
          {openSections[group.group_name] && (
            <div style={{ position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  left: "16px",
                  top: "29px",
                  bottom: "5px",
                  width: "2.5px",
                  background: "#2b8c29",
                  zIndex: 0,
                  margin: "5px",
                }}
                aria-hidden="true"
              />
              <ul className="sub-list1" aria-label={`${group.group_name} questions`}>
              {groupQuestions.map(q => {
                const dynIdx = visibleSteps.findIndex(dq => dq.node_number === q.node_number);
                if (dynIdx === -1) return null;
                const qStep = 2 + dynIdx;
                const isQActive = step === qStep;
                const continuousNumber = getContinuousNumber(q);
                const mainAns = answers[q.node_number];
                let displayText = getAnswerDisplayText(q);
                const isAnswered = mainAns !== undefined;
                const isNavigable = isQActive || isAnswered;
                return (
                  <li
                    key={q.node_number}
                    ref={isQActive ? activeStepRef : null}
                    style={{
                      color: isQActive ? "#323C43" : (isAnswered ? "#080808" : "#999"),
                      cursor: isNavigable ? "pointer" : "not-allowed",
                      fontWeight: "bold",
                      position: "relative",
                      zIndex: 1,
                    }}
                    onClick={isNavigable ? () => handleKeywordClick(qStep) : null}
                    onKeyDown={isNavigable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleKeywordClick(qStep); } } : null}
                    role={isNavigable ? "button" : undefined}
                    tabIndex={isNavigable ? "0" : undefined}
                    aria-label={`${q.keywords || 'Question'}: ${displayText || 'not answered'}`}
                  >
                    <span
                      className="step-number"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "24px",
                        height: "24px",
                        background: "#fbfcfc",
                        borderRadius: "50%",
                        fontSize: "14px",
                        margin: "9px 3px 4px 10px",
                      }}
                      aria-hidden="true"
                    >
                      {continuousNumber}
                    </span>
                    <div className="content-row" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
                      <span className="keyword-text">{getPanelKeywordLabel(q)}</span>
                      <span
                        style={{ color: "#999", backgroundColor: "#fbfcfc", border: "none", fontWeight: "bold", marginLeft: "4px", marginRight: "4px" }}
                        aria-hidden="true"
                      >
                        -
                      </span>
                      <p style={{ fontWeight: "normal" }}><RichText html={displayText || ""} /></p>
                    </div>
                  </li>
                );
              })}
              {isLastGroup && !pathwayHasEndInstruction && (
                <li
                  style={{ color: isSummaryPageActive ? "#323C43" : "#999", cursor: "pointer", fontWeight: "bold", position: "relative", zIndex: 1, marginTop: "10px" }}
                  onClick={() => setStep(totalSteps + 1)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStep(totalSteps + 1); } }}
                  role="button"
                  tabIndex="0"
                  aria-label="Go to Suggested Interventions"
                >
                  <span
                    className="step-number"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      background: "#fff",
                      borderRadius: "50%",
                      fontSize: "14px",
                      margin: "9px 3px 4px 10px",
                    }}
                    aria-hidden="true"
                  >
                    {visibleSteps.length + 4}
                  </span>
                  <div className="content-row" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", minWidth: 0, paddingRight: '15px' }}>
                    <span className="keyword-text">Review your answers</span>
                    <span style={{ color: "#999", backgroundColor: "white", border: "none", fontWeight: "bold", marginLeft: "4px", marginRight: "4px" }} aria-hidden="true"></span>
                    <p style={{ fontWeight: "normal" }}>{isSummaryPageActive ? "    " : ""}</p>
                  </div>
                </li>
              )}
            </ul>
            </div>
          )}
          {(isGroupDone || isSummaryPageActive) && <div className="connector11 done" aria-hidden="true" />}
        </div>
      );
    })
  );

  // -- Left-panel "Infant & caregiver context" block (step 1) ---------------
  const renderInfantContextPanel = () => (
    <div className="final-section">
      <div className={`infant-panel ${showInfantInfo ? "expanded" : ""}`}>
        <div
          className="infant-header"
          onClick={() => setShowInfantInfo(!showInfantInfo)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowInfantInfo(!showInfantInfo); } }}
          role="button"
          tabIndex="0"
          aria-expanded={showInfantInfo}
          aria-label="Infant and caregiver context information"
        >
          <span className="infant-info-btn" aria-hidden="true">
            <FaInfo size={14} aria-hidden="true" />
          </span>
          <div className="infant-heading">
            <span className="heading-line" />
            <span className="heading-text">
              Infant and
              <br />
              caregiver
              <br />
              context
            </span>
          </div>
        </div>
        {showInfantInfo && (
          <div className="infant-content">
            <p>
              Understanding the infant, family and their context, including available
              supports and resources, is essential to selecting realistic, feasible
              and sustainable interventions. Effective intervention planning must
              integrate best-available evidence with family-centred practice and
              shared decision-making to ensure the chosen approach fits the infant's
              goals and everyday life.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // -- Left panel (all three states: review-goal, step 1, question steps) ----
  const renderLeftPanel = () => {
    if (showReviewGoalScreen) {
      return <div className="panel-block expanded">{renderDemographicsPanel({ refCircle: null, connector: false })}</div>;
    }
    if (step === 1) {
      return <>{renderInfantContextPanel()}</>;
    }
    return (
      <>
        {renderDemographicsPanel({ refCircle: step > 1 ? activeStepRef : null, connector: true })}
        {renderPathwayGroupPanels()}
      </>
    );
  };

  // -- Step 1: appointment radio group (Initial / Review / Review Smart Goal)
  // The three options are mutually exclusive: picking one immediately
  // disables the other two, so a session can never be started under two
  // appointment types at once. When the page was opened from a specific
  // entry point (?appointment=...), ONLY that option is rendered - same
  // condition applies to all three entry points (Initial, Review, Review
  // Smart Goal) - so e.g. "Start Initial Client Assessment" never shows the
  // Review options. A direct /start-assessment visit (no param) keeps all
  // three. -------------------------------------------------------------------
  const offeredAppointments = entryAppointment
    ? [entryAppointment]
    : APPOINTMENT_OPTIONS;

  // `optionLabelSuffix` optionally appends extra text to a specific option's
  // label (keyed by appointment value). Review Smart Goal uses it to show the
  // goal being reviewed directly on the option, e.g.
  // "Review Smart Goal First Initial Smart Goal and Review Smart Goal sec time".
  const renderAppointmentOptions = (heading, optionLabelSuffix) => (
    <div className="question-card">
      <p className="question">{heading}</p>
      <div className="radio-group">
        {offeredAppointments.map((s) => (
          <label key={s}>
            <input
              type="radio"
              name="session"
              value={s}
              onChange={handleSessionChange}
              checked={session === s}
              disabled={!!session && session !== s}
            />
            {s}
            {optionLabelSuffix && optionLabelSuffix[s] ? ` ${optionLabelSuffix[s]}` : ""}
          </label>
        ))}
      </div>
    </div>
  );

  // -- Shared searchable client picker (Review + Review Smart Goal) ---------
  const renderClientPicker = (questionText) => {
    const term = (clientSearchTerm || "").trim().toLowerCase();
    // Match on the displayed "Name - Code" label so the box accepts either
    // half of it. The label always starts with the name, so a name-only search
    // still behaves exactly as before.
    const matches = reviewClientOptions.filter(name =>
      String(reviewClientLabels[name] || name).toLowerCase().includes(term)
    );
    return (
      <div className="question-card">
        <p className="question">{questionText}</p>
        <div className="age-dropdown-container" style={{ position: "relative" }}>
          <label hidden htmlFor="client-search" className="age-label">Client</label>
          <input
            id="client-search"
            type="text"
            className="age-select-dropdown1"
            placeholder={reviewClientOptions.length === 0 ? "Loading clients..." : "Search or select client"}
            value={clientSearchTerm}
            onChange={(e) => { setClientSearchTerm(e.target.value); setShowClientDropdown(true); }}
            onFocus={() => setShowClientDropdown(true)}
            onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
            autoComplete="off"
          />
          {showClientDropdown && (
            <ul style={{ listStyle: "none", margin: 0, padding: "4px 0", position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #ddd", borderRadius: "6px", maxHeight: "170px", overflowY: "auto", zIndex: 20, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
              {matches.map((name, idx) => (
                <li key={idx} style={{ padding: "8px 12px", cursor: "pointer" }}
                  onMouseDown={() => {
                    // `client` state stays the bare name - that is the value
                    // save_basic_details.php and the ?client= param expect.
                    setClient(name);
                    setClientSearchTerm(reviewClientLabels[name] || name);
                    setAgeRange(getReviewAgeForClient(name));
                    setSelectedReviewSessionId(null);
                    setShowClientDropdown(false);
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f0f7f0"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >{reviewClientLabels[name] || name}</li>
              ))}
              {matches.length === 0 && (
                <li style={{ padding: "8px 12px", color: "#999" }}>No matching clients</li>
              )}
            </ul>
          )}
        </div>
      </div>
    );
  };

  // -- Shared age-group picker (Review + Review Smart Goal) -----------------
  // Uses reviewAgeGroups, which is derived from pathwaysData filtered by the
  // EXACT appointment type that is running - "review" for Review, "review smart
  // goal" for Review Smart Goal. Each appointment therefore only offers the age
  // groups that actually have a pathway of its own kind.
  //
  // The selected value is NOT force-added any more. Doing so is what leaked
  // between the two appointments: a Review age group carried into Review Smart
  // Goal (or vice versa) was spliced into the list, so both appointments ended
  // up offering the same ages. The validation effect below clears a value the
  // current appointment cannot honour instead, so the therapist always picks
  // from the list instead of silently running on another appointment's pathway.
  const renderAgePicker = (questionText) => {
    const allAgeGroups = [...reviewAgeGroups];
    return (
      <div className="question-card">
        <p className="question">{questionText}</p>
        <div className="age-dropdown-container">
          <label hidden htmlFor="age-group-select" className="age-label">Age Group</label>
          <select id="age-group-select" name="ageGroup" className="age-select-dropdown"
            value={ageRange}
            onChange={(e) => setAgeRange(e.target.value)}
            aria-required="true">
            <option value="" disabled>{client ? "Select age group" : "Select a client first"}</option>
            {allAgeGroups.map((age, idx) => (
              <option key={idx} value={age}>{age}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  // -- Initial age-group picker (Initial pathway only) ----------------------
  // Uses initialAgeGroups which is derived from pathwaysData filtered by
  // appointment_type === "initial". This ensures the dropdown only shows
  // age groups that have an initial pathway available.
  const renderInitialAgePicker = (questionText) => (
    <div className="question-card">
      <p className="question">{questionText}</p>
      <div className="age-dropdown-container">
        <label hidden htmlFor="age-group-select" className="age-label">Age Group</label>
        <select id="age-group-select" name="ageGroup" className="age-select-dropdown"
          value={ageRange}
          onChange={(e) => setAgeRange(e.target.value)}
          aria-required="true">
          <option value="" disabled>Select Age Group</option>
          {initialAgeGroups.map((ageObj, idx) => (
            <option key={idx} value={ageObj.age_group}>{ageObj.age_group}</option>
          ))}
        </select>
      </div>
    </div>
  );

  // -- Step 1: Initial-session basic details form ---------------------------
  const renderInitialStep1 = () => (
    <>
      {renderAppointmentOptions("1. Initial session of new Client")}
      <div className="question-card">
        <p className="question">2. Client - please type infant's first name only.</p>
        <input type="text" placeholder="Enter First Name" className="input-boxq2" value={client} aria-label="Infant's first name" onChange={e => {
          const value = e.target.value;
          const nameRegex = /^[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF 'ÃƒÂ¢Ã¢â€šÂ¬Ã¢â€žÂ¢-]*$/;
          if (!nameRegex.test(value)) {
            setToast({ message: "Special characters not allowed", type: "error" });
            return;
          }
          setClient(value);
        }} />
      </div>
      {renderInitialAgePicker("3. Age group - please select current age group of infant.")}
    </>
  );

  // -- Step 1: Review-session basic details form ----------------------------
  const renderReviewStep1 = () => (
    <>
      {renderAppointmentOptions(
        urlParams.get("fromGoalReview") === "1"
          ? "1. Review the existing goal of the client in this session"
          : "1. Review Session to Set New Goal for Existing Client"
      )}
      {renderClientPicker("2. Client - please select infant's client.")}
      {renderAgePicker("3. Age group - What is the child's current corrected age?")}
    </>
  );

  // -- Review Smart Goal: the goal text shown ON the appointment option ------
  // Exactly the "Smart Goal" column of the row the therapist clicked Review
  // on, e.g. "First Initial Smart Goal and Review Smart Goal sec time". It is
  // appended to the "Review Smart Goal" radio label (see renderSmartGoalStep1)
  // so the option itself reads the whole line. No domain labels or source
  // metadata are shown.
  const smartGoalText = useMemo(() => {
    const ctx = smartGoalContext;
    if (!ctx || !ctx.status) return "";
    const texts = (Array.isArray(ctx.goals) ? ctx.goals : [])
      .map((g) => cleanGoalDomainText(g?.smartGoal || ""))
      .filter(Boolean);
    const fallback = [...new Set((Array.isArray(ctx.goalDomainTags) ? ctx.goalDomainTags : [])
      .map(cleanGoalDomainText)
      .filter(Boolean))];
    return (texts.length ? texts : fallback).join(", ");
  }, [smartGoalContext]);

  // -- Step 1: Review Smart Goal basic details form --------------------------
  // Same client/age selection as Review, but it launches the smart-goal
  // pathway which reviews (and later revises) the goal that already exists
  // on the originating pathway. ---------------------------------------------
  const renderSmartGoalStep1 = () => (
    <>
      {renderAppointmentOptions(
        "1. Review the existing Smart Goal of the client in this session",
        smartGoalText ? { [APPT_REVIEW_SMART_GOAL]: smartGoalText } : null
      )}
      {renderClientPicker("2. Client - please select infant's client.")}
      {renderAgePicker("3. Age group - What is the child's current corrected age?")}
      {smartGoalLoading && (
        <div className="question-card">
          <p className="question">Loading the existing goal..</p>
        </div>
      )}
    </>
  );

  // -- Goal-detail step block (domain sub-options) --------------------------

  // -- Goal-detail step block (domain sub-options) --------------------------
  const renderGoalDetailStep = () => {
    if (!(step >= 2 && isGoalDetailStep && visibleSteps[step - 2])) return null;
    return (
      <div className="question-card">
        <p className="question">Select the specific option from the list that supports the caregiver-led goal. Some domains do not have a list to select from - if so, no further selection is needed for that domain. Your selected domain(s) and option will determine the tailored information provided at the end of the pathway.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          {visibleSteps[step - 2].options.map(opt => {
            const isSelected = Array.isArray(answers[visibleSteps[step - 2].node_number]) && answers[visibleSteps[step - 2].node_number].includes(opt.option_text);
            if (!isSelected) return null;
            const subOpts = opt.sub_options || [];
            const domainAnswerKey = `${visibleSteps[step - 2].node_number}_${opt.option_text}_sub`;
            const currentSelections = answers[domainAnswerKey] || [];
            return (
              <div key={opt.option_id} style={{ border: "1px solid #fff", borderRadius: "8px", padding: "15px", backgroundColor: "#fff" }}>
                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "15px" : "0px" }}>
                  <div style={{ width: isMobile ? "100%" : "30%", fontWeight: "bold", display: "flex", alignItems: "start", gap: "10px" }}>
                    <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}>
                      <input type="checkbox" checked={isSelected} onChange={() => {
                        const qNode = visibleSteps[step - 2].node_number;
                        const allowed = handleMainOptionSelect(qNode, opt, true, true);
                        if (!allowed) return;
                        let curr = Array.isArray(answers[qNode]) ? [...answers[qNode]] : [];
                        let next = isSelected ? curr.filter(s => s !== opt.option_text) : [...curr, opt.option_text];
                        let allIds = visibleSteps[step - 2].options.filter(o => next.includes(o.option_text)).map(o => o.option_id);
                        saveResponseAPI(currentSessionId, qNode, "option", visibleSteps[step - 2].question_id, null, allIds.length === 0 ? [] : allIds);
                      }} /> <RichText inline html={opt.option_text} />
                    </label>
                  </div>
                  <div style={{ width: isMobile ? "100%" : "70%" }}>
                    {subOpts.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {subOpts.map(sub => {
                          const isSubSelected = currentSelections.includes(sub.option_text);
                          return (
                            <label key={sub.option_id} style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer" }}>
                              <input type="radio" value={sub.option_text} checked={isSubSelected}
                                onChange={() => {
                                  const qNode = visibleSteps[step - 2].node_number;
                                  handleSubOptionSelect(qNode, opt.option_text, sub, false, 1);
                                  if (!isSubSelected) saveResponseAPI(currentSessionId, sub.node_number, "option", sub.option_id, sub.option_text);
                                  else saveResponseAPI(currentSessionId, sub.node_number, "question", sub.option_id, "");
                                }} />
                              <span style={{ lineHeight: "1.4" }}><RichText inline html={sub.option_text} /></span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // -- Right-content footer navigation bar ----------------------------------
  const renderFooterButtons = () => (
    <div className="footer-buttons">
      <button className="backbutton" onClick={handleBack} disabled={step === 1 && !isGoalDetailStep}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
              <button className="save-btn" onClick={async () => { await handleSaveDraft(); navigate("/continue-assessment"); }}>Save for Later</button>
      <button className={`footer-btn next-btn ${isFormValid() ? "active" : ""}`} disabled={!isFormValid()} onClick={handleNext}>
        {"Next "}<FontAwesomeIcon icon={faArrowRight} />
      </button>
    </div>
  );

  // ---------------------------------------------------------------------------
  // MAIN RENDER
  // ---------------------------------------------------------------------------

  return (
    <>
      {isRestoringDraft && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(255,255,255,0.8)", zIndex: 9999, display: "flex", justifyContent: "center", alignItems: "center", fontSize: "20px", fontWeight: "bold" }}>
          Restoring Assessment...
        </div>
      )}

      {isGeneratingPdf && (
        <div role="status" aria-live="polite" style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(255,255,255,0.85)", zIndex: 10000, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "16px" }}>
          <div style={{ width: "48px", height: "48px", border: "5px solid #e0e0e0", borderTop: "5px solid #2b8c29", borderRadius: "50%", animation: "spin 0.9s linear infinite" }} />
          <p style={{ fontSize: "18px", fontWeight: "bold", color: "#2b8c29" }}>Generating PDF, please wait...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {showShareModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Share assessment summary"
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10001, display: "flex", justifyContent: "center", alignItems: "center" }}
          onClick={() => setShowShareModal(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", minWidth: "300px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: "16px", alignItems: "stretch" }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", color: "#222", textAlign: "center" }}>Share Assessment Summary</h3>
            <p style={{ margin: 0, fontSize: "13px", color: "#666", textAlign: "center" }}>The summary will be converted to PDF and shared via your chosen app.</p>

            <button
              onClick={handleShareMail}
              style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 20px", border: "1.5px solid #e0e0e0", borderRadius: "10px", background: "#f7f7f7", cursor: "pointer", fontSize: "15px", fontWeight: "600", color: "#333", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#eaf4ea"}
              onMouseLeave={e => e.currentTarget.style.background = "#f7f7f7"}
            >
              <span style={{ fontSize: "26px" }}>??</span>
              Share via Mail
            </button>

            <button
              onClick={handleShareWhatsApp}
              style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 20px", border: "1.5px solid #e0e0e0", borderRadius: "10px", background: "#f7f7f7", cursor: "pointer", fontSize: "15px", fontWeight: "600", color: "#333", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#eaf4ea"}
              onMouseLeave={e => e.currentTarget.style.background = "#f7f7f7"}
            >
              <span style={{ fontSize: "26px" }}>??</span>
              Share via WhatsApp
            </button>

            <button
              onClick={() => setShowShareModal(false)}
              style={{ marginTop: "4px", background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: "14px", textAlign: "center" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {networkError && (
        <div
          role="alert"
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10002, display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", minWidth: "300px", maxWidth: "360px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", textAlign: "center" }}
          >
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", color: "#c0392b" }}>Network Issue</h3>
            <p style={{ margin: "12px 0 20px", fontSize: "14px", color: "#555", lineHeight: 1.5 }}>
              We're having trouble reaching the server. Please check your internet connection and try again.
            </p>
            <button
              onClick={() => { setNetworkError(false); retryPendingResponses(); }}
              style={{ padding: "10px 28px", borderRadius: "8px", border: "none", background: "#2b8c29", color: "#fff", fontWeight: "600", fontSize: "14px", cursor: "pointer" }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showGoalDomainPopup && (
        <div
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10002, display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", minWidth: "300px", maxWidth: "360px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", textAlign: "center" }}
          >
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", color: "#c0392b" }}>Selection Not Allowed</h3>
            <p style={{ margin: "12px 0 20px", fontSize: "14px", color: "#555", lineHeight: 1.5 }}>
              Eating and Drinking domain can only be selected as a single choice.
            </p>
            <button
              onClick={() => setShowGoalDomainPopup(false)}
              style={{ padding: "10px 28px", borderRadius: "8px", border: "none", background: "#2b8c29", color: "#fff", fontWeight: "600", fontSize: "14px", cursor: "pointer" }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div className="assessment1">
        <div className="header-bar1">
          <img src={Frame} alt="" className="frame-icon" onClick={() => setShowLeftPanel(prev => !prev)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowLeftPanel(prev => !prev); } }} role="button" tabIndex="0" aria-label="Toggle navigation panel" aria-expanded={showLeftPanel} />
          <h1 tabIndex="0">Background and Assessment Information</h1>
          <h1 className="header-title-short" tabIndex="0">Assessments</h1>

          <div className="header-actions">
            <button className="header-btn restart-btn" onClick={handleRestart} aria-label="Restart assessment">
              <img src={restart} alt="" className="header-icon" aria-hidden="true" />
              <span className="header-btn-text">Restart</span>
            </button>
            {(step >= 2 || showReviewGoalScreen) && (
              <button
                className="header-btn fav-btn"
                onClick={handleToggleFavourite}
                aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
                aria-pressed={isFavourite}
              >
                <FontAwesomeIcon
                  icon={faStar}
                  className="star-icon1"
                  aria-hidden="true"
                  style={{
                    color: isFavourite ? "#FFD700" : "#fff",
                  }}
                />
                <span className="header-btn-text">Favourite</span>
              </button>
            )}
          </div>
        </div>

        <div className="assessment-container">
          {/* PREVIOUS DATA: while the folder icon has the history open, the whole
              assessment area is replaced by one full-width pane instead of
              rendering the history inside the 75% right-hand column. */}
          {isHistoryPaneOpen ? (
            /* The assessment footer (Back / Save for Later / Next) is deliberately
               NOT rendered here. Those buttons act on the live questionnaire -
               moving the step, or saving a half-finished assessment - and the
               questionnaire is not on screen while Previous Data is open, so the
               two views must not share a footer. The panel's own "Previous Page"
               and "X" are the only ways in and out. */
            <div className="right-content previous-data-pane">
              <div className="right-content-over" tabIndex="0" aria-label="Client history">
                {renderPreviousDataPanel()}
              </div>
            </div>
          ) : (
            <>
          <div className={`left-panel ${showLeftPanel ? "show-panel" : "hide-panel"}`} tabIndex="0">
            {renderLeftPanel()}
          </div>


          <div className="right-content">
            {showReviewGoalScreen ? (
              <>
                <div className="right-content-over" tabIndex="0" aria-label="Scrollable content area">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", height: "100%", padding: "40px 20px" }}>
                    <div style={{ background: "#ffffff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "28px 32px", maxWidth: "760px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                      <p style={{ color: "#288537", fontWeight: "bold", fontSize: "24px", textAlign: "center", lineHeight: "1.8", margin: 0 }}>
                        Review pathways use background and assessment information gathered to date, alongside goal progress, to provide tailored guidance.
                      </p>
                      <p style={{ color: "#626A6F", fontWeight: "normal", fontSize: "14px", textAlign: "center", lineHeight: "1.8", margin: "18px 0 0" }}>
                        Please click below to update goal progress.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="footer-buttons">
                  <button className="backbutton" onClick={handleBack}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
                  <button className="footer-btn next-btn active" onClick={() => navigate(`/goalreview?sessionId=${selectedReviewSessionId || currentSessionId}`)}>
                    Goal Progress Update
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="right-content-over" tabIndex="0" aria-label="Scrollable content area">

                  {!historyGroupName && (
                    <>
                      {step === 1 && session === APPT_INITIAL && renderInitialStep1()}

                      {step === 1 && session === APPT_REVIEW && renderReviewStep1()}

                      {step === 1 && session === APPT_REVIEW_SMART_GOAL && renderSmartGoalStep1()}

                      {/* Safety net: a bare /start-assessment with no entry
                          point must never leave step 1 blank - offer the
                          three appointment types to pick from. */}
                      {step === 1 && !session && renderAppointmentOptions("1. Select the appointment type")}

                      {renderCurrentQuestion()}

                      {renderGoalDetailStep()}
                    </>
                  )}

                  {step === (1 + visibleSteps.length + 1) && !isGoalDetailStep && (
                    <>
                      <div className="summ-container1" ref={summaryRef}>
                        <div className="sum-cont1">

                          {/* Section 1 - Row 1: Client name (centered, cpa color) */}
                          <div className="summ-client-row">
                            {interventionsData?.session?.client_name || client}
                          </div>

                          {/* Section 1 - Row 2: Metadata pills */}
                          <div className="summ-meta-row">
                            <span className="summ-meta-pill summ-meta-plain">
                              <span className="summ-meta-label" style={{ color: "#247A33", fontSize: "16px" }}>Assessment Date:</span>&nbsp;
                              <span className="summ-meta-value" style={{ color: "#247A33" }}>
                                {(() => {
                                  const code = String(interventionsData?.session?.client_code || "");
                                  const digits = code.replace(/\D/g, "").slice(0, 8);
                                  if (digits.length === 8) {
                                    const dd = digits.slice(0, 2);
                                    const mm = digits.slice(2, 4);
                                    const yyyy = digits.slice(4, 8);
                                    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                    const mIdx = parseInt(mm, 10) - 1;
                                    return `${parseInt(dd, 10)} ${months[mIdx]} ${yyyy}`;
                                  }
                                  return "";
                                })()}
                              </span>
                            </span>
                            <span className="summ-meta-pill summ-meta-green">
                              <span className="summ-meta-label" style={{ color: "#626A6F" }}>Client ID:</span>&nbsp;
                              <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.client_code || ""}</span>
                            </span>
                            <span className="summ-meta-pill summ-meta-green">
                              <span className="summ-meta-label" style={{ color: "#626A6F" }}>Age Group:</span>&nbsp;
                              <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.age_group || ageRange}</span>
                            </span>
                          </div>

                          {/* Section 1 - Row 3: Section title */}
                          <h3 className="summ-section-title">Interpreting Medical History and Combined Assessment Findings</h3>

                          {/* Section 1 - Row 4: Topography + Severity */}
                          <div className="summ-findings-block">
                            <div className="summ-finding-row">
                              <span className="icon-circle1"><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /></span>
                              <strong className="summ-finding-label">Topography:&nbsp;</strong>
                              <span className="summ-value-pill">
                                {(() => {
                                  const q = visibleSteps.find(v => v.question_text.toLowerCase().includes("topography"));
                                  if (!q) return "";
                                  const ans = answers[q.node_number];
                                  if (!ans) return "";
                                  const ansArray = Array.isArray(ans) ? ans : [ans];
                                  if (ansArray.length === 1) {
                                    const selectedOpt = q.options?.find(o => o.option_text === ansArray[0]);
                                    if (selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0) {
                                      const subAns = answers[`${q.node_number}_${ansArray[0]}_sub`];
                                      if (subAns) return Array.isArray(subAns) ? subAns.join(", ") : subAns;
                                    }
                                    return ansArray[0];
                                  }
                                  return ansArray.join(", ");
                                })()}
                              </span>
                            </div>

                            <div className="summ-finding-row">
                              <span className="icon-circle1"><FontAwesomeIcon icon={faSliders} aria-hidden="true" /></span>
                              <strong className="summ-finding-label">Severity:&nbsp;</strong>
                              <span className="summ-value-pill">
                                {(() => {
                                  const q = visibleSteps.find(v => v.question_text.toLowerCase().includes("severity"));
                                  const ans = q ? answers[q.node_number] : "";
                                  return Array.isArray(ans) ? ans.join(", ") : (ans || "");
                                })()}
                              </span>
                            </div>
                          </div>

                          {/* Section 1 - Row 5: Early indicators of comorbidities */}
                          <div className="summ-finding-row">
                            <span className="icon-circle1"><FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" /></span>
                            <strong className="summ-finding-label">Early indicators of comorbidities:&nbsp;</strong>
                            <span className="summ-value-pill">
                              {(() => {
                                const comorbidityGroup = activePathway?.groups?.find(g => g.group_name.toLowerCase().includes("comorbidities"));
                                if (!comorbidityGroup) return "";
                                let keywordNames = [];
                                (comorbidityGroup.questions || []).forEach(q => {
                                  const ans = answers[q.node_number];
                                  if (ans && ans.toString().toLowerCase() === "yes") keywordNames.push(q.keywords || q.question_text);
                                });
                                return keywordNames.length > 0 ? keywordNames.join(", ") : "";
                              })()}
                            </span>
                          </div>
                          <div className="summ-finding-row">
                            <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                            <strong className="summ-finding-label">Goal Limiting Factors:&nbsp;</strong>
                            <span className="summ-value-pill">
                              {(() => {
                                const factors = interventionsData?.session?.goal_limiting_factors || [];
                                return factors.join(", ");
                              })()}
                            </span>
                          </div>
                          {/* Section 1 - Row 6: Goals. Built from the suggested
                              interventions for this session ("domain - skill"
                              per group). The originating pathway's domain is only
                              used as a fallback so the row is never blank. */}
                          <div className="summ-finding-row">
                            <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                            <strong className="summ-finding-label">Goals:&nbsp;</strong>
                            <span className="summ-value-pill">
                              {(() => {
                                const originatingTags = (smartGoalContext?.goalDomainTags || [])
                                  .map((t) => cleanGoalDomainText(t))
                                  .filter(Boolean);

                                // Review Smart Goal: the Goals row must reflect
                                // the goal being reviewed - the originating
                                // session named by ?goalSessionId. Its domain-skill
                                // tags come from the smart-goal context and are
                                // authoritative, so a stale or unscoped
                                // intervention fetch can never leak another
                                // session's domains (e.g. the Initial's) in here.
                                if (isSmartGoalMode && originatingTags.length > 0) {
                                  return [...new Set(originatingTags)].join(", ");
                                }

                                const map = interventionsData?.data || {};
                                const tabs = Object.keys(map).map((key) => {
                                  const items = map[key] || [];
                                  const first = items[0] || {};
                                  const domain = first.goal_domain || key || "";
                                  const skill = first.goal_skill || "";
                                  return skill && skill !== "null" ? `${domain} - ${skill}` : domain;
                                });
                                const primary = [...new Set(tabs.filter(Boolean))];
                                if (primary.length > 0) {
                                  return primary.join(", ");
                                }
                                // Fall back to the originating session's own
                                // goals. `goalDomainTags` is built server-side
                                // from the PLANNING goals only, already split
                                // into "Domain - Skill" and de-duplicated.
                                // Joining smartGoalContext.goals here instead
                                // repeated every domain once per Goal Review
                                // and left legacy rows as a raw glued string.
                                return [...new Set(originatingTags)].join(", ");
                              })()}
                            </span>
                          </div>

                          {/* Review Smart Goal: the Goal Domains / Goals carried
                              over from the ORIGINATING pathway. Rendered as plain
                              finding rows directly after "Goal Limiting Factors" /
                              "Goals" so the therapist sees what is being reviewed
                              and revised before continuing to the Intervention
                              page. */}
                        


                          {/* Section 2 - Row 1: Info heading */}
                          <h4 className="summ-info-header">
                            You are about to receive suggested interventions based on your responses throughout this pathway.
                          </h4>

                          {/* Section 2 - Row 2: Info text */}
                          <p className="summ-info-text">
                            Review your answers now before confirming
                          </p>
                        </div>
                      </div>
                      <div className="summary-footer">
                        <button className="backbutton" onClick={handleBack} disabled={step === 1 && !isGoalDetailStep}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>

                        {/* Spec STEP 1.1 / 2.3: this single CTA is the hand-off
                            into /Interventions in every mode. The goal-setting
                            step ("Set Goal Procedure" / "Revise Selected
                            Intervention") lives on the /Interventions page, so
                            there is deliberately no second button here. */}
                        <button className=" summ-save-btn" onClick={handleSummaryConfirm}>
                          Save and View Intervention
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {step !== (1 + visibleSteps.length + 1) && renderFooterButtons()}
              </>
            )}
          </div>
            </>
          )}
          {renderHistoryPrintCopy()}
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* One-time Initial Assessment guard. An Initial Assessment can only be
          performed once per client, so offer the two supported ways forward
          instead of a dead end. */}
      {initialBlocked && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Initial Assessment already completed"
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10002, display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <div style={{ background: "#fff", borderRadius: "16px", padding: "28px 32px", maxWidth: "520px", width: "90%", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: "18px" }}>
            <h3 style={{ margin: 0, color: "#247A33", fontSize: "20px" }}>Initial Assessment already completed</h3>
            <p style={{ margin: 0, color: "#626A6F", fontSize: "14px", lineHeight: 1.7 }}>
              {initialBlocked.message
                || "An Initial Assessment has already been completed for this client. Please use Review to set a new goal or Review Smart Goal to review the existing goal."}
            </p>
            {initialBlocked.client_name && (
              <p style={{ margin: 0, color: "#626A6F", fontSize: "13px", lineHeight: 1.7 }}>
                Client: <strong>{initialBlocked.client_name}</strong>
                {initialBlocked.age_group ? <> &nbsp;|&nbsp; Age group: <strong>{initialBlocked.age_group}</strong></> : null}
                {initialBlocked.session_id ? <> &nbsp;|&nbsp; Existing Initial session: <strong>#{initialBlocked.session_id}</strong></> : null}
              </p>
            )}
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                className="backbutton"
                onClick={() => setInitialBlocked(null)}
              >
                Stay here
              </button>
              <button
                className="summ-save-btn"
                style={{ background: "#247A33", color: "#fff", border: "none", borderRadius: "6px", padding: "10px 18px", cursor: "pointer" }}
                onClick={() => {
                  const params = new URLSearchParams();
                  if (initialBlocked.client_name) params.set("client", initialBlocked.client_name);
                  if (initialBlocked.client_id) params.set("clientId", String(initialBlocked.client_id));
                  if (initialBlocked.age_group) params.set("age", initialBlocked.age_group);
                  params.set("appointment", "Review Smart Goal");
                  if (initialBlocked.session_id) params.set("goalSessionId", String(initialBlocked.session_id));
                  params.set("fromGoalReview", "1");
                  setInitialBlocked(null);
                  navigate(`/start-assessment?${params.toString()}`);
                }}
              >
                Review Smart Goal
              </button>
              <button
                className="summ-save-btn"
                style={{ background: "#fff", color: "#247A33", border: "1px solid #247A33", borderRadius: "6px", padding: "10px 18px", cursor: "pointer" }}
                onClick={() => {
                  const params = new URLSearchParams();
                  if (initialBlocked.client_name) params.set("client", initialBlocked.client_name);
                  if (initialBlocked.client_id) params.set("clientId", String(initialBlocked.client_id));
                  if (initialBlocked.age_group) params.set("age", initialBlocked.age_group);
                  params.set("appointment", "Review");
                  setInitialBlocked(null);
                  navigate(`/start-assessment?${params.toString()}`);
                }}
              >
                Review - Set New Goal
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

